<template>
  <div class="create-place-form">
    <div class="close-confirm-container" v-show="isShowCloseConfirm">
      <div class="close-confirm">
        <div class="body">
          <div class="text">{{ mode === 'CREATE' ? $t('CreatePlaceForm.closeCreateConfirmMessage') : $t('CreatePlaceForm.closeEditConfirmMessage') }}</div>
          <div class="button-container">
            <button class="outline" @click="clickCloseButton">{{ $t('CreatePlaceForm.ok') }}</button>
            <button @click="isShowCloseConfirm = false">{{ $t('CreatePlaceForm.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div class="loading-animation" v-show="isSaving && !isCompleted"></div>
    <div class="complete-animation" v-show="isCompleted"></div>
    <TitleBar :text="$t(`CreatePlaceForm.step${step}Topic`)" :showIconButton="false" :showCloseButton="!(mode === 'CREATE' && isShowAddPlaceImageForm)" @clickCloseButton="hasKeydown ? (isShowCloseConfirm = true) : (clickCloseButton())" v-show="!isSaving && !isCompleted" />

    <div class="verify-phone-number" v-if="showOtpDialog">
      <div class="dialog">
        <div class="loading-animation primary" v-show="isResendingOtp || isVerifying"></div>
        <span v-if="!isResendingOtp && !isExceedsLimitSmsPerDay && !isVerifying && !isExceedsLimitVerifyPerHour" class="title">
          {{ $t('CreatePlaceForm.enterVerificationCode') }}
        </span>
        <input v-if="!isResendingOtp && !isExceedsLimitSmsPerDay && !isVerifying && !isExceedsLimitVerifyPerHour" type="text" pattern="\d*" v-model="pin" @input="onInputPin" autocomplete="off" autocorrect="off" autocapitalize="off" maxlength="4" :readonly="isVerifying">
        <span v-if="!isResendingOtp && !isExceedsLimitSmsPerDay && !isVerifying && !isExceedsLimitVerifyPerHour" class="guide">
          {{ $t('CreatePlaceForm.guide') }} <span>{{ telList[telIndex].telNumber }}</span>
        </span>
        <span v-if="!isResendingOtp && !isExceedsLimitSmsPerDay && !isVerifying && !isExceedsLimitVerifyPerHour" class="guide">
          {{ $t('CreatePlaceForm.guide1') }}
        </span>
        <span v-if="!isResendingOtp && isExceedsLimitSmsPerDay" class="guide">
          {{ $t('CreatePlaceForm.guideExceedsLimitSmsPerDay') }}
        </span>
        <span v-if="!isResendingOtp && isFailedSendingSms" class="guide">
          {{ $t('CreatePlaceForm.guideFailedSendingSms') }}
        </span>
        <button v-if="!isResendingOtp && !isExceedsLimitSmsPerDay && !isVerifying && !isExceedsLimitVerifyPerHour" class="resend" @click="resendOtp(false)" :disabled="countDownResendOtp > 0">
          {{ $t('CreatePlaceForm.resendVerificationCode') }} <span>({{ countDownResendOtp }})</span>
        </button>
        <span v-if="!isVerifying && isExceedsLimitVerifyPerHour" class="guide error">
          {{ $t('CreatePlaceForm.guideExceedsLimitVerifyPerHour') }}
        </span>
        <span v-if="!isVerifying && isIncorrectVerificationCode" class="guide error">
          {{ $t('CreatePlaceForm.guideIncorrectVerificationCode') }}
        </span>
        <div class="action" v-if="!isVerifying">
          <button class="close" v-if="isExceedsLimitSmsPerDay || isExceedsLimitSmsPerDay" @click="cancelOtp">{{ $t('CreatePlaceForm.closeVerificationCode') }}</button>
          <button class="cancel" v-if="!isResendingOtp && !isExceedsLimitSmsPerDay" @click="cancelOtp">{{ $t('CreatePlaceForm.cancelVerificationCode') }}</button>
          <button class="submit" v-if="!isResendingOtp && !isExceedsLimitSmsPerDay" :disabled="isDisableSubmitOtp" @click="submitOtp">{{ $t('CreatePlaceForm.submitOtp') }}</button>
        </div>
      </div>
    </div>

    <div class="body" :class="{'hide-place-name': category.id === '-1'}" v-show="!isSaving && !isCompleted">
      <div class="detail" v-show="step === 1">
        <div class="field">
          <label>{{ $t('CreatePlaceForm.placeCategory') }}</label>
          <div class="input-list">
            <PlaceCategoryDropdown ref="place-category" class="place-category-dropdown" v-model="category" :class="{'alert-required': category.id === '' && hasFirstClickCreateButton}" />
          </div>
        </div>
        <div class="field" v-if="category.id !== '-1'">
          <label>{{ $t('CreatePlaceForm.placeName') }}</label>
          <div class="input-list">
            <TextFieldSuggestWidget
              id="primary-name"
              class="text-field-suggest"
              v-model="primaryName"
              :placeholder="`${$t('CreatePlaceForm.placePrimaryName')} *`"
              :suggests="[]"
              :isInvalid="invalidPrimaryName && (hasFirstChangePrimaryName || hasFirstClickCreateButton)"
              :guide="$i18n.locale === 'en' && primaryName !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
              :class="{
                'alert-required': invalidPrimaryName && (hasFirstChangePrimaryName || hasFirstClickCreateButton)
              }"
            />
            <TextFieldSuggestWidget
              id="secondary-name"
              class="text-field-suggest"
              v-model="secondaryName"
              :placeholder="`${$t('CreatePlaceForm.placeSecondaryName')} *`"
              :suggests="[]"
              :isInvalid="invalidSecondaryName && (hasFirstChangeSecondaryName || hasFirstClickCreateButton)"
              :guide="$i18n.locale === 'th' && secondaryName !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
              :class="{
                'alert-required': invalidSecondaryName && (hasFirstChangeSecondaryName || hasFirstClickCreateButton)
              }"
            />
          </div>
        </div>
        <div class="more-detail" :class="{active: showMoreDetail, 'can-add-image': mode === 'CREATE' && isAuthenticated}">
          <div class="field">
            <label>{{ $t('CreatePlaceForm.placeTel') }}</label>
            <div class="input-list">
              <div v-for="(tel, index) in telList" :key="`tel-${index}`" :ref="`tel-${index}`" class="tel-row" :class="{selected: tel.selected, 'alert-required': tel.invalidTel && hasFirstClickCreateButton}">
                <input type="text" v-model="tel.telNumber" @input="changeTelNumber(index)" :placeholder="$t('CreatePlaceForm.examplePlaceTel')" :class="{'alert-required': tel.invalidTel && hasFirstClickCreateButton}">
                <div class="confirm-verification" v-if="tel.isRequired && tel.isVerified" :class="{'alert-required': tel.invalidTel && hasFirstClickCreateButton}">
                  <img :src="util.getImagePath('confirm_verification@2x.png')" :srcset="util.getImageSet('confirm_verification')">
                </div>
                <button :ref="`tel-${index}-require-verification`" v-if="tel.isRequired && !tel.isVerified" @click="verifyPhonNumber(index)" class="require-verification-button" :class="{'alert-required': tel.invalidTel && hasFirstClickCreateButton}">
                  <img :src="util.getImagePath('require_verification@2x.png')" :srcset="util.getImageSet('require_verification')">
                  <div class="guide">{{ $t('CreatePlaceForm.requireVerificationGuide') }}</div>
                </button>
                <button @click="deleteTel(index)" v-if="telList.length > 1" :class="{'alert-required': tel.invalidTel && hasFirstClickCreateButton}">
                  <img :style="{visibility: telList.length > 1 ? 'visible' : 'hidden'}" :src="util.getImagePath('delete_light_black@2x.png')" :srcset="util.getImageSet('delete_light_black')">
                </button>
                <button v-if="canPromptPai(index) || true" class="promptpai-icon" @click="setPromptPai(index)" :class="{'alert-required': tel.invalidTel && hasFirstClickCreateButton}">
                  <img :src="util.getImagePath('promptpai_icon@2x.png')" :srcset="util.getImageSet('promptpai_icon')">
                </button>
              </div>
            </div>
            <transition name="fade">
            <div class="tel-guide" v-show="showTelGuide">{{ $t('CreatePlaceForm.telGuide') }}</div>
            </transition>
          </div>
          <button class="add-tel" @click="addTel">{{ $t('CreatePlaceForm.addPlaceTel') }}</button>

          <div class="two-fields-in-row" v-show="isThailand">
            <div class="field" style="width: 50%;">
              <label>{{ $t('CreatePlaceForm.houseNo') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget id="house-no" class="text-field-suggest" v-model="houseNo" :suggests="[]" />
              </div>
            </div>
            <div class="field" style="width: 50%;">
              <label>{{ $t('CreatePlaceForm.moo') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget id="moo" class="text-field-suggest" v-model="moo" :suggests="[]" />
              </div>
            </div>
          </div>

          <div class="field" v-show="isThailand">
            <label class="small">{{ $t('CreatePlaceForm.village') }}</label>
            <div class="input-list">
              <TextFieldSuggestWidget
                v-if="isPrimaryMode"
                id="primary-village"
                class="text-field-suggest"
                v-model="primaryVillage"
                :placeholder="$t('CreatePlaceForm.primaryVillage')"
                :suggests="[]"
                :class="{'alert-required': invalidPrimaryVillage && (hasFirstChangePrimaryVillage || hasFirstClickCreateButton)}"
                :isInvalid="invalidPrimaryVillage && (hasFirstChangePrimaryVillage || hasFirstClickCreateButton)"
                :guide="$i18n.locale === 'en' && primaryVillage !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
              />
              <TextFieldSuggestWidget
                v-if="!isPrimaryMode"
                id="secondary-village"
                class="text-field-suggest"
                v-model="secondaryVillage"
                :placeholder="$t('CreatePlaceForm.secondaryVillage')"
                :suggests="[]"
                :class="{'alert-required': invalidSecondaryVillage && (hasFirstChangeSecondaryVillage || hasFirstClickCreateButton)}"
                :isInvalid="invalidSecondaryVillage && (hasFirstChangeSecondaryVillage || hasFirstClickCreateButton)"
                :guide="$i18n.locale === 'th' && secondaryVillage !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
              />
            </div>
          </div>

          <div class="field" v-show="false && isThailand && refPlaceList.length > 0">
            <label>{{ $t('CreatePlaceForm.refPlaceName') }}</label>
            <div class="input-list">
              <DropdownWidget ref="place-category" class="ref-place-dropdown" v-model="refPlace" :options="refPlaceList" :placeholder="$t('CreatePlaceForm.refPlaceNamePlaceholder')" />
            </div>
          </div>

          <div class="two-fields-in-row" v-show="isThailand">
            <div class="field">
              <label class="small">{{ $t('CreatePlaceForm.condo') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-soi"
                  class="text-field-suggest"
                  v-model="primaryCondo"
                  :placeholder="`${$t('CreatePlaceForm.primaryCondo')}`"
                  :suggests="[]"
                  :class="{'alert-required': invalidPrimaryCondo && (hasFirstChangePrimaryCondo || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimaryCondo && (hasFirstChangePrimaryCondo || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primaryCondo !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-condo"
                  class="text-field-suggest"
                  v-model="secondaryCondo"
                  :placeholder="`${$t('CreatePlaceForm.secondaryCondo')}`"
                  :suggests="[]"
                  :class="{'alert-required': invalidSecondaryCondo && (hasFirstChangeSecondaryCondo || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondaryCondo && (hasFirstChangeSecondaryCondo || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondaryCondo !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>

            <div class="field">
              <label>{{ $t('CreatePlaceForm.building') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-building"
                  class="text-field-suggest"
                  v-model="primaryBuilding"
                  :placeholder="$t('CreatePlaceForm.primaryBuilding')"
                  :suggests="[]"
                  :class="{'alert-required': invalidPrimaryBuilding && (hasFirstChangePrimaryBuilding || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimaryBuilding && (hasFirstChangePrimaryBuilding || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primaryBuilding !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-building"
                  class="text-field-suggest"
                  v-model="secondaryBuilding"
                  :placeholder="$t('CreatePlaceForm.secondaryBuilding')"
                  :suggests="[]"
                  :class="{'alert-required': invalidSecondaryBuilding && (hasFirstChangeSecondaryBuilding || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondaryBuilding && (hasFirstChangeSecondaryBuilding || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondaryBuilding !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>
          </div>

          <div class="two-fields-in-row" v-show="isThailand">
            <div class="field" style="width: 50%;">
              <label>{{ $t('CreatePlaceForm.floor') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget id="floor" class="text-field-suggest" v-model="floor" :suggests="[]" />
              </div>
            </div>
            <div class="field" style="width: 50%;">
              <label>{{ $t('CreatePlaceForm.roomNo') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget id="room-no" class="text-field-suggest" v-model="roomNo" :suggests="[]" />
              </div>
            </div>
          </div>

          <div class="two-fields-in-row" v-show="isThailand">
            <div class="field">
              <label>{{ $t('CreatePlaceForm.soi') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-soi"
                  class="text-field-suggest"
                  v-model="primarySoi"
                  :placeholder="`${$t('CreatePlaceForm.primarySoi')}`"
                  :suggests="[]"
                  :class="{'alert-required': invalidPrimarySoi && (hasFirstChangePrimarySoi || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimarySoi && (hasFirstChangePrimarySoi || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primarySoi !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-soi"
                  class="text-field-suggest"
                  v-model="secondarySoi"
                  :placeholder="`${$t('CreatePlaceForm.secondarySoi')}`"
                  :suggests="[]"
                  :class="{'alert-required': invalidSecondarySoi && (hasFirstChangeSecondarySoi || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondarySoi && (hasFirstChangeSecondarySoi || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondarySoi !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>

            <div class="field">
              <label>{{ $t('CreatePlaceForm.road') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-road"
                  class="text-field-suggest"
                  v-model="primaryRoad"
                  :placeholder="$t('CreatePlaceForm.primaryRoad')"
                  :suggests="[]"
                  :class="{'alert-required': invalidPrimaryRoad && (hasFirstChangePrimaryRoad || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimaryRoad && (hasFirstChangePrimaryRoad || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primaryRoad !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-road"
                  class="text-field-suggest"
                  v-model="secondaryRoad"
                  :placeholder="$t('CreatePlaceForm.secondaryRoad')"
                  :suggests="[]"
                  :class="{'alert-required': invalidSecondaryRoad && (hasFirstChangeSecondaryRoad || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondaryRoad && (hasFirstChangeSecondaryRoad || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondaryRoad !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>
          </div>

          <div class="two-fields-in-row" v-show="isThailand">
            <div class="field">
              <label>{{ subdistrictPlaceholder }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-subdistrict"
                  class="text-field-suggest"
                  v-model="primarySubdistrict"
                  :placeholder="`${$t('CreatePlaceForm.primarySubdistrict')} *`"
                  :suggests="primarySubdistrictList.map(s => s.name)"
                  @select="onSelectPrimarySubdistrictSuggest"
                  :class="{'alert-required': invalidPrimarySubdistrict && (hasFirstChangePrimarySubdistrict || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimarySubdistrict && (hasFirstChangePrimarySubdistrict || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primarySubdistrict !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                  />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-subdistrict"
                  class="text-field-suggest"
                  v-model="secondarySubdistrict"
                  :placeholder="`${$t('CreatePlaceForm.secondarySubdistrict')} *`"
                  :suggests="secondarySubdistrictList.map(s => s.name)"
                  @select="onSelectSecondarySubdistrictSuggest"
                  :class="{'alert-required': invalidSecondarySubdistrict && (hasFirstChangeSecondarySubdistrict || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondarySubdistrict && (hasFirstChangeSecondarySubdistrict || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondarySubdistrict !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>
            <div class="field">
              <label>{{ districtPlaceholder }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-district"
                  class="text-field-suggest"
                  v-model="primaryDistrict"
                  :placeholder="`${$t('CreatePlaceForm.primaryDistrict')} *`"
                  :suggests="primaryDistrictList.map(d => d.name)"
                  @select="onSelectPrimaryDistrictSuggest"
                  :class="{'alert-required': invalidPrimaryDistrict && (hasFirstChangePrimaryDistrict || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimaryDistrict && (hasFirstChangePrimaryDistrict || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primaryDistrict !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-district"
                  class="text-field-suggest"
                  v-model="secondaryDistrict"
                  :placeholder="`${$t('CreatePlaceForm.secondaryDistrict')} *`"
                  :suggests="secondaryDistrictList.map(d => d.name)"
                  @select="onSelectSecondaryDistrictSuggest"
                  :class="{'alert-required': invalidSecondaryDistrict && (hasFirstChangeSecondaryDistrict || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondaryDistrict && (hasFirstChangeSecondaryDistrict || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondaryDistrict !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>
          </div>

          <div class="two-fields-in-row" v-show="isThailand">
            <div class="field">
              <label>{{ $t('CreatePlaceForm.province') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  v-if="isPrimaryMode"
                  id="primary-province"
                  class="text-field-suggest"
                  v-model="primaryProvince"
                  :placeholder="`${$t('CreatePlaceForm.primaryProvince')} *`"
                  :suggests="primaryProvinceList.map(p => p.name)"
                  @select="onSelectPrimaryProvinceSuggest"
                  :class="{'alert-required': invalidPrimaryProvince && (hasFirstChangePrimaryProvince || hasFirstClickCreateButton)}"
                  :isInvalid="invalidPrimaryProvince && (hasFirstChangePrimaryProvince || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'en' && primaryProvince !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
                <TextFieldSuggestWidget
                  v-if="!isPrimaryMode"
                  id="secondary-province"
                  class="text-field-suggest"
                  v-model="secondaryProvince"
                  :placeholder="`${$t('CreatePlaceForm.secondaryProvince')} *`"
                  :suggests="secondaryProvinceList.map(p => p.name)"
                  @select="onSelectSecondaryProvinceSuggest"
                  :class="{'alert-required': invalidSecondaryProvince && (hasFirstChangeSecondaryProvince || hasFirstClickCreateButton)}"
                  :isInvalid="invalidSecondaryProvince && (hasFirstChangeSecondaryProvince || hasFirstClickCreateButton)"
                  :guide="$i18n.locale === 'th' && secondaryProvince !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
                />
              </div>
            </div>
            <div class="field">
              <label>{{ $t('CreatePlaceForm.postcode') }}</label>
              <div class="input-list">
                <TextFieldSuggestWidget
                  id="postcode"
                  class="text-field-suggest"
                  v-model="postcode"
                  :suggests="[]"
                  :class="{'alert-required': !(/^\d{5}?$/g.test(postcode)) && (hasFirstChangePostcode|| hasFirstClickCreateButton)}"
                  :isInvalid="!(/^\d{5}?$/g.test(postcode)) && (hasFirstChangePostcode|| hasFirstClickCreateButton)"
                />
              </div>
            </div>
          </div>

          <div class="field">
            <label>{{ $t('CreatePlaceForm.addressDetail') }}</label>
            <div class="input-list">
              <div id="primary-address-detail" class="textarea" v-if="isPrimaryMode">
                <textarea
                  ref="primary-address-detail"
                  type="text"
                  v-model="primaryAddressDetail"
                  :placeholder="$t('CreatePlaceForm.primaryAddressDetail')"
                  :class="{'alert-required': invalidPrimaryAddressDetail && (hasFirstChangePrimaryAddressDetail || hasFirstClickCreateButton)}"
                ></textarea>
                <div class="invalid" v-if="invalidPrimaryAddressDetail && (hasFirstChangePrimaryAddressDetail || hasFirstClickCreateButton)">
                  <img :src="util.getImagePath('require_verification@2x.png')" :srcset="util.getImageSet('require_verification')">
                  <div class="guide">{{ $i18n.locale === 'en' && primaryAddressDetail !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : '' }}</div>
                </div>
              </div>
              <div id="secondary-address-detail" class="textarea" v-if="!isPrimaryMode">
                <textarea
                  ref="secondary-address-detail"
                  type="text"
                  v-model="secondaryAddressDetail"
                  :placeholder="$t('CreatePlaceForm.secondaryAddressDetail')"
                  :class="{'alert-required': invalidSecondaryAddressDetail && (hasFirstChangeSecondaryAddressDetail || hasFirstClickCreateButton)}"
                ></textarea>
                <div class="invalid" v-if="invalidSecondaryAddressDetail && (hasFirstChangeSecondaryAddressDetail || hasFirstClickCreateButton)">
                  <img :src="util.getImagePath('require_verification@2x.png')" :srcset="util.getImageSet('require_verification')">
                  <div class="guide">{{ $i18n.locale === 'th' && secondaryAddressDetail !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : '' }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label>{{ $t('CreatePlaceForm.country') }}</label>
            <div class="input-list">
              <TextFieldSuggestWidget
                v-if="isPrimaryMode"
                id="primary-country"
                class="text-field-suggest"
                v-model="primaryCountry"
                :placeholder="`${$t('CreatePlaceForm.primaryCountry')} *`"
                :suggests="countryList.map(c => c[`name${$i18n.locale === 'th' ? '' : 'En'}`])"
                @select="onSelectPrimaryCountrySuggest"
                :class="{'alert-required': invalidPrimaryCountry && (hasFirstChangePrimaryCountry || hasFirstClickCreateButton)}"
                :isInvalid="invalidPrimaryCountry && (hasFirstChangePrimaryCountry || hasFirstClickCreateButton)"
                :guide="$i18n.locale === 'en' && primaryCountry !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
              />
              <TextFieldSuggestWidget
                v-if="!isPrimaryMode"
                id="secondary-country"
                class="text-field-suggest"
                v-model="secondaryCountry"
                :placeholder="`${$t('CreatePlaceForm.secondaryCountry')} *`"
                :suggests="countryList.map(c => c[`name${$i18n.locale === 'th' ? 'En' : ''}`])"
                @select="onSelectSecondaryCountrySuggest"
                :class="{'alert-required': invalidSecondaryCountry && (hasFirstChangeSecondaryCountry || hasFirstClickCreateButton)}"
                :isInvalid="invalidSecondaryCountry && (hasFirstChangeSecondaryCountry || hasFirstClickCreateButton)"
                :guide="$i18n.locale === 'th' && secondaryCountry !== '' ? $t('CreatePlaceForm.doNotUserThaiCharacter') : ''"
              />
            </div>
          </div>

          <div class="field" style="display: flex; flex-wrap: wrap; justify-content: center;">
            <button class="input-mode-button" v-if="($i18n.locale === 'th' && isPrimaryMode) || ($i18n.locale === 'en' && !isPrimaryMode)" @click="isPrimaryMode = $i18n.locale === 'en'">{{ $t('CreatePlaceForm.enModeInput') }}</button>
            <button class="input-mode-button" v-if="($i18n.locale === 'th' && !isPrimaryMode) || ($i18n.locale === 'en' && isPrimaryMode)" @click="isPrimaryMode = $i18n.locale === 'th'">{{ $t('CreatePlaceForm.thModeInput') }}</button>
            <button v-if="mode === 'CREATE' && isAuthenticated" class="add-image-button" @click="clickManageImage">{{ 0 > imageList.length ? $t('CreatePlaceForm.addImage') : `${$t('CreatePlaceForm.manageImage')}${imageList.length > 1 && $i18n.locale === 'en' ? 's' : ''} (${imageList.length})` }}</button>
            <input v-if="mode === 'CREATE' && isAuthenticated" ref="add-image-input" class="add-image-input" type="file" accept="image/*" @change="manageImageList()" v-show="false" multiple>
          </div>

          <button class="more-detail-button" :class="{active: showMoreDetail, 'can-add-image': mode === 'CREATE' && isAuthenticated}" v-if="showMoreDetail" @click="showMoreDetail = false">
            <img :src="util.getImagePath('arrow_up_white@2x.png')" :srcset="util.getImageSet('arrow_up_white')">
          </button>
        </div>
        <button class="more-detail-button" style="position: absolute;" :class="{active: showMoreDetail, 'can-add-image': mode === 'CREATE' && isAuthenticated}" v-if="!showMoreDetail" @click="showMoreDetail = true">
          {{ showMoreDetail ? '' : $t('CreatePlaceForm.moreDetail') }}{{ mode === 'CREATE' && isAuthenticated ? `, ${$t('CreatePlaceForm.addImage')}` : '' }}
          <img :src="util.getImagePath('arrow_up_white@2x.png')" :srcset="util.getImageSet('arrow_up_white')" style="position: absolute;" :class="{down: !showMoreDetail}">
        </button>
      </div>

      <div ref="checkDetail" class="check-detail" v-show="step === 2">
        <div class="field warning-privacy">
          <div class="icon-container">
            <img :src="util.getImagePath('require_verification@2x.png')" :srcset="util.getImageSet('require_verification')">
          </div>
          {{ $t('CreatePlaceForm.warningPrivacy') }}
        </div>

        <div class="map-snippet" style="height: 180px; margin-bottom: 10px; background-color: rgb(238, 235, 228);">
          <img v-if="step === 2" style="height: 100%; width: 100%; object-fit: cover;" :src="`//mmmap15.longdo.com/mmmap/snippet/?locale=th&zoom=17&mode=icons&map=epsg3857&lat=${location.lat}&long=${location.lon}&width=600&height=180&marker=position:${location.lat},${location.lon};icon:https://promptpai.com/static/image_asset/my_place_pin_blue.png;offsetX:center;offsetY:bottom;`" border=0>
        </div>

        <div style="font-size: 18px; line-height: 1.5; font-weight: bold;" v-if="category.id !== '-1'">{{ primaryName }}</div>
        <div style="line-height: 1.5; color: rgb(120, 120, 120); font-weight: bold; margin-bottom: 10px;" v-if="category.id !== '-1'">{{ secondaryName }}</div>
        <div style="display: flex; flex-direction: row; align-items: center; margin-bottom: 10px;">
          <img :src="category.iconfilename_hd" style="max-width: 16px; max-height: 16px; margin-left: 2.5px; margin-right: 7.5px;">
          {{ $root.$i18n.locale === 'th' ? category.description_th : category.description_en }}
        </div>
        <div class="field" v-if="telList.filter(t => t.telNumber).length > 0">
          <div class="icon-container">
            <img :src="util.getImagePath('tel_dark_blue@2x.png')" :srcset="util.getImageSet('tel_dark_blue')">
          </div>
          <div style="line-height: 1.5;">{{ telList.map(t => t.telNumber !== '').length > 0 ? telList.map(t => t.telNumber).join(', ') : $t('CreatePlaceForm.emptyTel') }}</div>
        </div>
        <div class="field" v-if="primaryAddress || secondaryAddress">
          <div class="icon-container">
            <img :src="util.getImagePath('icon_place_blue@2x.png')" :srcset="util.getImageSet('icon_place_blue')">
          </div>
          <div>
            <div style="line-height: 1.5;">{{ primaryAddress }}</div>
            <div style="line-height: 1.5; color: rgb(120, 120, 120);">{{ secondaryAddress }}</div>
          </div>
        </div>
        <div class="field" v-if="primaryAddressDetail || secondaryAddressDetail">
          <div class="icon-container">
            <img :src="util.getImagePath('icon_info_blue.svg')">
          </div>
          <div>
            <div style="line-height: 1.5;">{{ primaryAddressDetail }}</div>
            <div style="line-height: 1.5; color: rgb(120, 120, 120);">{{ secondaryAddressDetail }}</div>
          </div>
        </div>
        <div class="image-list" v-show="imageList.length > 0">
          <div class="image" v-for="(image, i) in imageList" :key="`image${i}`" @click="clickImage(i)">
            <img :src="image.preview">
          </div>
        </div>
      </div>
      <button ref="lock-location-button" class="lock-location-button" @click="isLockLocation = !isLockLocation" v-show="step === 1">
        <img :src="util.getImagePath(isLockLocation ? 'lock_btn@2x.png' : 'unlock_btn@2x.png')" :srcset="util.getImageSet(isLockLocation ? 'lock_btn' : 'unlock_btn')">
      </button>
      <img ref="edit-location-pin" class="edit-location-pin" v-show="!isLockLocation" :src="util.getImagePath('my_place_pin_blue@2x.png')" :srcset="util.getImageSet('my_place_pin_blue')">
      <div ref="create-place-map" id="create-place-map" :style="mapStyle"></div>
      <button ref="current-location-button" class="current-location-button" @click="clickCurrentLocationButton">
        <img :src="util.getImagePath('here_white@2x.png')" :srcset="util.getImageSet('here_white')">
      </button>
      <button ref="zoom-in-button" class="zoom-in-button" @click="clickZoomInButton">
        <img :src="util.getImagePath('zoom_in_white@2x.png')" :srcset="util.getImageSet('zoom_in_white')">
      </button>
      <button ref="zoom-out-button" class="zoom-out-button" @click="clickZoomOutButton">
        <img :src="util.getImagePath('zoom_out_white@2x.png')" :srcset="util.getImageSet('zoom_out_white')">
      </button>
      <div class="button-list">
        <button class="create-button" v-if="step === 1" @click="clickCreateButton">
          {{ mode === 'CREATE' ? $t('CreatePlaceForm.createPlace') : $t('CreatePlaceForm.savePlace') }}
        </button>
        <button class="back-button" v-if="step === 2" @click="clickBackButton">
          {{ $t('CreatePlaceForm.back') }}
        </button>
        <button class="confirm-button" v-if="step === 2" @click="clickConfirmButton" :disabled="countDownConfirm > 0">
          {{ $t('CreatePlaceForm.confirm') }} {{ countDownConfirm > 0 ? `(${countDownConfirm})` : ''}}
        </button>
      </div>
    </div>
    <div class="add-place-image-container" v-show="mode === 'CREATE' && isShowAddPlaceImageForm">
      <div class="image" v-for="(image, i) in imageList" :key="`image-${i}`">
        <img :src="image.preview" @click="clickImage(i)">
        <div class="row">
          <img class="edit-icon" :src="util.getImagePath(`edit_light_black@2x.png`)" :srcset="util.getImageSet(`edit_light_black`)" @click="clickImage(i)">
          <input type="text" v-model="image.comment" :placeholder="$t('AddPlaceImageForm.commentPlaceholder')">
          <button class="delete-button" @click="deleteImage(i)">
            <img :src="util.getImagePath(`delete_light_black@2x.png`)" :srcset="util.getImageSet(`delete_light_black`)">
          </button>
        </div>
      </div>
      <div class="image add" @click="clickAddImage">
        <img :src="util.getImagePath('add_photo_blue@2x.png')" :srcset="util.getImageSet('add_photo_blue')">
      </div>
    </div>
    <button class="back-button on-add-place-image-container" v-show="mode === 'CREATE' && isShowAddPlaceImageForm" @click="isShowAddPlaceImageForm = false">
      {{ $t('CreatePlaceForm.back') }}
    </button>
  </div>
</template>

<script>
import TitleBar from '@/components/TitleBar.vue'
import PlaceCategoryDropdown from '@/components/PlaceCategoryDropdown.vue'
import DropdownWidget from '@/components/DropdownWidget.vue'
import TextFieldSuggestWidget from '@/components/TextFieldSuggestWidget.vue'
import { setTimeout, clearTimeout } from 'timers'
import axios from 'axios'

export default {
  name: 'CreatePlaceForm',
  components: {
    TitleBar,
    PlaceCategoryDropdown,
    DropdownWidget,
    TextFieldSuggestWidget
  },
  props: {
    location: {
      type: Object,
      default: () => {
        return {
          lat: 13.764955,
          lon: 100.538317
        }
      }
    },
    promptpaiDescription: {
      type: Object,
      default: undefined
    },
    mode: {
      type: String,
      default: 'EDIT'
    }
  },
  data () {
    return {
      step: 1,
      isPrimaryMode: true,
      myPin: null,
      category: {
        id: this.mode === 'CREATE' ? '' : `${this.promptpaiDescription.iconfileid === '64' ? '-1' : this.promptpaiDescription.iconfileid}`
      },
      primaryName: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_name_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_name_en),
      isEditedPrimaryName: false,
      secondaryName: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_name_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_name_th),
      isEditedSecondaryName: false,
      showMoreDetail: true,
      telList: [],
      houseNo: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.house_no,
      moo: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.moo,
      primaryRefPlaceName: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.parent_name_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.parent_name_en),
      secondaryRefPlaceName: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.parent_name_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.parent_name_th),
      refPlace: {
        value: this.mode === 'CREATE' ? '' : (this.promptpaiDescription.promptpai_address.parent_promptpai_id !== '' ? `A${this.promptpaiDescription.promptpai_address.parent_promptpai_id}` : ''),
        label: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.parent_name_th,
        labelEn: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.parent_name_en
      },
      refPlaceList: [],
      floor: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.floor,
      roomNo: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.room,
      primaryVillage: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.village_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.village_en),
      secondaryVillage: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.village_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.village_th),
      primaryCondo: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.condo_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.condo_en),
      secondaryCondo: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.condo_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.condo_th),
      primaryBuilding: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.building_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.building_en),
      secondaryBuilding: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.building_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.building_th),
      primarySoi: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.alley_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.alley_en),
      secondarySoi: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.alley_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.alley_th),
      primaryRoad: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.road_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.road_en),
      secondaryRoad: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.road_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.road_th),
      primaryCountry: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.country_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.country_en),
      secondaryCountry: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.country_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.country_th),
      countryList: [],
      suggestSubdistrictTask: null,
      suggestDistrictTask: null,
      suggestProvinceTask: null,
      geocode: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.geocode,
      primarySubdistrict: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.subdistrict_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.subdistrict_en),
      secondarySubdistrict: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.subdistrict_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.subdistrict_th),
      primarySubdistrictList: [],
      secondarySubdistrictList: [],
      subdistrictCode: '',
      primaryDistrict: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.district_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.district_en),
      secondaryDistrict: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.district_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.district_th),
      primaryDistrictList: [],
      secondaryDistrictList: [],
      districtCode: '',
      primaryProvince: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.province_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.province_en),
      secondaryProvince: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.province_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.province_th),
      primaryProvinceList: [],
      secondaryProvinceList: [],
      provinceCode: '',
      postcode: this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.postcode,
      primaryAddressDetail: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.address_detail_th) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.address_detail_en),
      secondaryAddressDetail: this.$i18n.locale === 'th' ? (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.address_detail_en) : (this.mode === 'CREATE' ? '' : this.promptpaiDescription.promptpai_address.address_detail_th),
      createPlaceMap: undefined,
      hasFirstClickCreateButton: false,
      hasFirstChangePrimaryName: false,
      hasFirstChangeSecondaryName: false,
      hasFirstChangePrimaryVillage: false,
      hasFirstChangeSecondaryVillage: false,
      hasFirstChangePrimaryCondo: false,
      hasFirstChangeSecondaryCondo: false,
      hasFirstChangePrimaryBuilding: false,
      hasFirstChangeSecondaryBuilding: false,
      hasFirstChangePrimarySoi: false,
      hasFirstChangeSecondarySoi: false,
      hasFirstChangePrimaryRoad: false,
      hasFirstChangeSecondaryRoad: false,
      hasFirstChangePrimarySubdistrict: false,
      hasFirstChangeSecondarySubdistrict: false,
      hasFirstChangePrimaryDistrict: false,
      hasFirstChangeSecondaryDistrict: false,
      hasFirstChangePrimaryProvince: false,
      hasFirstChangeSecondaryProvince: false,
      hasFirstChangePostcode: false,
      hasFirstChangePrimaryAddressDetail: false,
      hasFirstChangeSecondaryAddressDetail: false,
      hasFirstChangePrimaryCountry: false,
      hasFirstChangeSecondaryCountry: false,
      mapStyle: {},
      isSaving: false,
      isCompleted: false,
      isLockLocation: true,
      isShowAddPlaceImageForm: false,
      imageList: [],
      isShowCloseConfirm: false,
      showOtpDialog: false,
      telIndex: null,
      pin: '',
      countDownResendOtpTask: null,
      countDownResendOtp: 0,
      isVerifying: false,
      showTelGuide: false,
      isResendingOtp: false,
      isExceedsLimitSmsPerDay: false,
      isFailedSendingSms: false,
      isExceedsLimitVerifyPerHour: false,
      isIncorrectVerificationCode: false,
      hasKeydown: false,
      countDownConfirm: 5,
      countDownConfirmTask: null
    }
  },
  computed: {
    isAuthenticated () {
      var self = this
      return JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
    },
    isDisableSubmitOtp () {
      var self = this
      const r = /^[0-9]{4}$/
      return !r.test(self.pin)
    },
    isThailand () {
      var self = this
      return self.primaryCountry === 'ไทย' || self.primaryCountry === 'ประเทศไทย' || self.primaryCountry === 'Thailand' || self.primaryCountry === 'thailand'
    },
    primaryAddress () {
      var self = this
      if (!self.isThailand) {
        return self.primaryCountry
      }
      var address = []
      address.push(self.houseNo)
      address.push(self.moo !== '' ? `${self.$t('CreatePlaceForm.moo')} ${self.moo}` : '')
      // address.push(self.refPlace.value !== '' ? self.refPlace[self.$i18n.locale === 'th' ? 'label' : 'labelEn'] : '')
      address.push(self.primaryVillage)
      address.push(self.primaryCondo)
      address.push(self.primaryBuilding)
      address.push(self.floor !== '' ? `${self.$t('CreatePlaceForm.floor')} ${self.floor}` : '')
      address.push(self.roomNo !== '' ? `${self.$t('CreatePlaceForm.roomNo')} ${self.roomNo}` : '')
      address.push(self.primarySoi)
      address.push(self.primaryRoad)
      address.push(self.primarySubdistrict)
      address.push(self.primaryDistrict)
      address.push(self.primaryProvince)
      address.push(self.postcode)
      address.push(self.primaryCountry)
      address = address.filter(a => a !== '')

      var joiner = self.$root.$i18n.locale === 'th' ? ' ' : ', '
      return address.join(joiner)
    },
    secondaryAddress () {
      var self = this
      if (!self.isThailand) {
        return self.secondaryCountry
      }
      var address = []
      var lang = self.$root.$i18n.locale === 'th' ? 'en' : 'th'
      address.push(self.houseNo)
      address.push(self.moo !== '' ? `${self.$t('CreatePlaceForm.moo', lang)} ${self.moo}` : '')
      address.push(self.secondaryVillage)
      address.push(self.secondaryCondo)
      address.push(self.secondaryBuilding)
      // address.push(self.refPlace.value !== '' ? self.refPlace[lang === 'th' ? 'label' : 'labelEn'] : '')
      address.push(self.floor !== '' ? `${self.$t('CreatePlaceForm.floor', lang)} ${self.floor}` : '')
      address.push(self.roomNo !== '' ? `${self.$t('CreatePlaceForm.roomNo', lang)} ${self.roomNo}` : '')
      address.push(self.secondarySoi)
      address.push(self.secondaryRoad)
      address.push(self.secondarySubdistrict)
      address.push(self.secondaryDistrict)
      address.push(self.secondaryProvince)
      address.push(self.postcode)
      address.push(self.secondaryCountry)
      address = address.filter(a => a !== '')

      var joiner = lang === 'th' ? ' ' : ', '
      return address.join(joiner)
    },
    subdistrictPlaceholder () {
      var self = this
      const bkk = ['กรุงเทพ', 'กรุงเทพฯ', 'กรุงเทพมหานคร', 'กทม.', 'Bangkok', 'bangkok', 'bkk', 'BKK', 'Bkk']
      if (bkk.includes(self.primaryProvince) || bkk.includes(self.secondaryProvince)) {
        return self.$t('CreatePlaceForm.khweang')
      }
      return self.$t('CreatePlaceForm.subdistrict')
    },
    districtPlaceholder () {
      var self = this
      const bkk = ['กรุงเทพ', 'กรุงเทพฯ', 'กรุงเทพมหานคร', 'กทม.', 'Bangkok', 'bangkok', 'bkk', 'BKK', 'Bkk']
      if (bkk.includes(self.primaryProvince) || bkk.includes(self.secondaryProvince)) {
        return self.$t('CreatePlaceForm.khet')
      }
      return self.$t('CreatePlaceForm.district')
    },
    invalidPrimaryName () {
      var self = this
      return (self.primaryName === '' && self.category.id !== '-1') ||
        (self.primaryName !== '' && self.category.id !== '-1' && self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryName))
    },
    invalidSecondaryName () {
      var self = this
      return (self.secondaryName === '' && self.category.id !== '-1') ||
        (self.secondaryName !== '' && self.category.id !== '-1' && self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryName))
    },
    invalidPrimaryVillage () {
      var self = this
      return self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryVillage)
    },
    invalidSecondaryVillage () {
      var self = this
      return self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryVillage)
    },
    invalidPrimaryCondo () {
      var self = this
      return self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryCondo)
    },
    invalidSecondaryCondo () {
      var self = this
      return self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryCondo)
    },
    invalidPrimaryBuilding () {
      var self = this
      return self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryBuilding)
    },
    invalidSecondaryBuilding () {
      var self = this
      return self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryBuilding)
    },
    invalidPrimarySoi () {
      var self = this
      return self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primarySoi)
    },
    invalidSecondarySoi () {
      var self = this
      return self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondarySoi)
    },
    invalidPrimaryRoad () {
      var self = this
      return self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryRoad)
    },
    invalidSecondaryRoad () {
      var self = this
      return self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryRoad)
    },
    invalidPrimarySubdistrict () {
      var self = this
      return self.primarySubdistrict === '' ||
        (self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primarySubdistrict))
    },
    invalidSecondarySubdistrict () {
      var self = this
      return self.secondarySubdistrict === '' ||
        (self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondarySubdistrict))
    },
    invalidPrimaryDistrict () {
      var self = this
      return self.primaryDistrict === '' ||
        (self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryDistrict))
    },
    invalidSecondaryDistrict () {
      var self = this
      return self.secondaryDistrict === '' ||
        (self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryDistrict))
    },
    invalidPrimaryProvince () {
      var self = this
      return self.primaryProvince === '' ||
        (self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryProvince))
    },
    invalidSecondaryProvince () {
      var self = this
      return self.secondaryProvince === '' ||
        (self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryProvince))
    },
    invalidPrimaryAddressDetail () {
      var self = this
      return self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryAddressDetail)
    },
    invalidSecondaryAddressDetail () {
      var self = this
      return self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryAddressDetail)
    },
    invalidPrimaryCountry () {
      var self = this
      return self.primaryCountry === '' ||
        (self.$i18n.locale === 'en' && /[\u0E00-\u0E7F']/.test(self.primaryCountry))
    },
    invalidSecondaryCountry () {
      var self = this
      return self.secondaryCountry === '' ||
        (self.$i18n.locale === 'th' && /[\u0E00-\u0E7F']/.test(self.secondaryCountry))
    }
  },
  mounted () {
    var self = this
    window.addEventListener('keydown', self.flagHasKeydown)
    self.initialMap()
    self.getCountryList()
    if (self.mode === 'CREATE') {
      self.addTel()
      self.initialAddress(self.location)
    } else if (self.mode === 'EDIT') {
      self.promptpaiDescription.telephone_list.forEach(t => {
        self.addTel()
        self.telList[self.telList.length - 1].selected = t.pp_id === self.promptpaiDescription.promptpai_id
        self.telList[self.telList.length - 1].telNumber = `${t.tel_no}${t.tel_no_to !== '' ? `-${t.tel_no_to}` : ''}${t.ext !== '' ? ` ${self.$t('PlaceCard.ext')} ${t.ext}` : ''}`
        self.telList[self.telList.length - 1].isVerified = t.verified
        self.telList[self.telList.length - 1].isRequired = t.required_to_verify
      })
      if (self.telList.length === 0) {
        self.addTel()
      }
    }
    self.$root.$on('rotate', self.rotate)
    self.$root.$on('hFlip', self.hFlip)
    self.$root.$on('vFlip', self.vFlip)
  },
  methods: {
    flagHasKeydown () {
      var self = this
      self.hasKeydown = true
    },
    clickCloseButton () {
      var self = this
      self.$router.back()
    },
    initialMap () {
      var self = this
      if (window.longdo) {
        self.createPlaceMap = new window.longdo.Map({
          placeholder: document.getElementById('create-place-map'),
          language: self.$i18n.locale,
          lastView: false
        })
        self.createPlaceMap.Event.bind('ready', () => { // When map is ready to use
          self.createPlaceMap.Ui.Fullscreen.visible(true)

          self.createPlaceMap.Ui.BottomRight.classList.add('custom-bottom-right-map-ui')
          self.createPlaceMap.Ui.BottomRight.insertBefore(self.$refs['zoom-out-button'], self.createPlaceMap.Ui.BottomRight.firstChild)
          self.createPlaceMap.Ui.BottomRight.insertBefore(self.$refs['zoom-in-button'], self.createPlaceMap.Ui.BottomRight.firstChild)
          self.createPlaceMap.Ui.BottomRight.insertBefore(self.$refs['current-location-button'], self.createPlaceMap.Ui.BottomRight.firstChild)
        })
        self.createPlaceMap.Event.bind('drop', () => { // When map is ready to use
          if (!self.isLockLocation) {
            self.initialAddress(self.createPlaceMap.location())
            self.getRefPlaceSuggestList(self.createPlaceMap.location(), self.refPlace.value)
          }
        })
        self.createPlaceMap.location(self.location)
        self.createPlaceMap.Ui.DPad.visible(false)
        self.createPlaceMap.Ui.Geolocation.visible(false)
        self.createPlaceMap.Ui.LayerSelector.visible(false)
        self.createPlaceMap.Ui.Toolbar.visible(false)
        self.createPlaceMap.Ui.Zoombar.visible(false)
        self.createPlaceMap.Ui.Scale.visible(false)
        self.createPlaceMap.resize()

        var pinOption = {
          icon: {
            url: self.util.getImagePath('my_place_pin_blue.png'),
            urlHD: self.util.getImagePath('my_place_pin_blue@2x.png'),
            offset: {
              x: 27 / 2,
              y: 39
            }
          }
        }
        self.myPin = new window.longdo.Marker(self.location, pinOption)
        self.createPlaceMap.Overlays.add(self.myPin)

        self.createPlaceMap.placeholder().insertBefore(self.$refs['edit-location-pin'], self.createPlaceMap.placeholder().firstChild)
        self.createPlaceMap.Ui.TopRight.style.display = 'flex'
        self.createPlaceMap.Ui.TopRight.style.flexDirection = 'row'
        self.createPlaceMap.Ui.TopRight.insertBefore(self.$refs['lock-location-button'], self.createPlaceMap.Ui.TopRight.firstChild)

        if (self.mode === 'EDIT') {
          // กรณีที่ข้อมูลเดิมก่อนจะเริ่มทำการแก้ไข ไม่มีข้อมูลตำบล อำเภอ จังหวัด ทั้งภาษาไทยและอังกฤษ (แม้ extract address แล้วก็แกะออกมาไม่ได้) จึงจำเป็นต้องไป get address จาก lat, lon มาเติมให้
          if ((self.primarySubdistrict === '' && self.primaryDistrict === '' && self.primaryProvince === '') && (self.secondarySubdistrict === '' && self.secondaryDistrict === '' && self.secondaryProvince === '')) {
            self.initialAddress(self.location)
          }

          // ไม่น่าจะจำเป็นแล้ว เพราะทำ extract address ที่ฝั่ง web client แล้ว
          // if (self.promptpaiDescription.promptpai_address.province_th === '') { // ถ้าว่างๆ ก็คาดการได้ว่าข้อมูลสถานที่เป็นแบบเก่า คือเก็บข้อมูลใน address string ยาวๆ
          //   self.initialAddress(self.location) // ไปหา address มา fill ให้ตามช่องต่างๆ

          //   // เดิมทีผู้ใช้อาจกรอกเลขที่บ้าน หมู่ ซอย รวมเข้าไปใน address ยาวๆ จึงต้องเอามาแปะใน address detail เพื่อให้ข้อมูลไม่หายไป
          //   if (self.promptpaiDescription.promptpai_address.address_detail_th === '' && self.promptpaiDescription.promptpai_address.address_detail_en === '') {
          //     self.primaryAddressDetail = self.promptpaiDescription.promptpai_address[`address_${self.$i18n.locale}`]
          //     self.secondaryAddressDetail = self.promptpaiDescription.promptpai_address[`address_${self.$i18n.locale === 'th' ? 'en' : 'th'}`]
          //   }
          // }
        }

        self.getRefPlaceSuggestList(self.createPlaceMap.location(), self.mode === 'CREATE' ? false : (self.refPlace.value || false))

        var dolLayer = new window.longdo.Layer('', {
          type: window.longdo.LayerType.Custom,
          url: function (projection, tile, zoom, hd) {
            return `https://ms.longdo.com/mmmap/img.php?mode=dol_hd&proj=${projection.longdoName}&zoom=${zoom}&x=${tile.u}&y=${tile.v}`
          },
          zoomRange: {
            min: 17,
            max: 20
          },
          source: {
            max: 19
          },
          opacity: 0.145
        })
        self.createPlaceMap.Layers.add(dolLayer)
      }
    },
    getCountryList () {
      var self = this
      axios({
        method: 'get',
        url: '/static/country.json'
      }).then(result => {
        self.countryList = result.data
      }).catch(error => {
        console.log(error)
      })
    },
    clickCurrentLocationButton () {
      var self = this
      self.createPlaceMap.location('GEOLOCATION')
    },
    clickZoomInButton () {
      var self = this
      var zoom = self.createPlaceMap.zoom()
      self.createPlaceMap.zoom(++zoom, true)
    },
    clickZoomOutButton () {
      var self = this
      var zoom = self.createPlaceMap.zoom()
      self.createPlaceMap.zoom(--zoom, true)
    },
    clickManageImage () {
      var self = this
      if (self.imageList.length <= 0) {
        // self.$refs[`add-image-input`].click()
        self.clickAddImage()
      } else {
        self.isShowAddPlaceImageForm = true
      }
    },
    addTel () {
      var self = this
      self.telList.push({
        selected: false,
        telNumber: '',
        isVerified: false,
        isRequired: false,
        isAvailable: true,
        invalidTel: false
      })
    },
    changeTelNumber (index) {
      var self = this
      self.telList[index].selected = false
      self.telList[index].isVerified = false
      self.telList[index].isRequired = false
      self.telList[index].isAvailable = true
      self.telList[index].invalidTel = false
      if (self.telList[index].telNumber.length >= 9) {
        self.checkPhoneNoVerificationStatus(index)
      }
    },
    deleteTel (index) {
      var self = this
      self.telList.splice(index, 1)
    },
    setPromptPai (index) {
      var self = this
      var temp = self.telList[index].selected
      self.telList.forEach(tel => {
        tel.selected = false
      })
      self.telList[index].selected = !temp && self.telList[index].isAvailable
    },
    isMobileNumber (value) {
      var rex = /^(\+66|66|0)(6|8|9)\d{8}?$/g
      return rex.test('' + value)
    },
    canPromptPai (index) {
      var self = this
      const isMobileNumber = self.isMobileNumber(self.telList[index].telNumber)
      const canPromptPai = (!isMobileNumber || ((self.isMobileNumber || self.telList[index].isRequired) && self.telList[index].isVerified)) && self.telList[index].isAvailable
      return canPromptPai
    },
    verifyPhonNumber (index) {
      var self = this
      self.telIndex = index
      self.isResendingOtp = false
      self.isVerifying = false
      self.showOtpDialog = true
      self.resendOtp(true)
    },
    resendOtp (isFirst = false) {
      var self = this
      if (self.isVerifying) {
        return false
      }
      self.isResendingOtp = true
      self.isExceedsLimitSmsPerDay = false
      self.isFailedSendingSms = false
      self.isExceedsLimitVerifyPerHour = false
      self.isIncorrectVerificationCode = false

      if (self.countDownResendOtpTask) {
        clearInterval(self.countDownResendOtpTask)
        self.countDownResendOtpTask = null
      }
      self.pin = ''

      var userProfile = {}
      var uuid = JSON.parse(self.getPromptPaiCookies('Uuid'))
      var isAuthenticated = JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
      if (isAuthenticated) {
        userProfile = JSON.parse(self.getPromptPaiCookies('UserProfile'))
        uuid = undefined
      }
      self.pp.sendSmsForPhoneNoVerification(
        'name' in userProfile ? userProfile.name : undefined,
        'longdousertoken' in userProfile ? userProfile.longdousertoken : undefined,
        isAuthenticated ? undefined : uuid,
        self.telList[self.telIndex].telNumber,
        isFirst ? 0 : 1,
        (result) => {
          if (result.status.code === 200 || self.telList[self.telIndex].telNumber === '026329700') {
            self.countDownResendOtp = 60
            self.countDownResendOtpTask = setInterval(() => {
              if (self.countDownResendOtp === 0) {
                clearInterval(self.countDownResendOtpTask)
                self.countDownResendOtpTask = null
                self.countDownResendOtp = 0
              } else {
                self.countDownResendOtp--
              }
            }, 1000)
          } else if (result.status.code === 401) {
            self.loginAgain()
          } else if (result.status.code === 403) {
          } else if (result.status.code === 503) {
            if (result.status.error_code === 'overlimit_sms') {
              self.isExceedsLimitSmsPerDay = true
            }
            if (result.status.error_code === 'sendsms_failed') {
              self.isFailedSendingSms = true
            }
          } else {
          }
          self.isResendingOtp = false
        }
      )
    },
    onInputPin () {
      var self = this
      self.isExceedsLimitVerifyPerHour = false
      self.isIncorrectVerificationCode = false
    },
    cancelOtp () {
      var self = this
      self.showOtpDialog = false
      if (self.countDownResendOtpTask) {
        clearInterval(self.countDownResendOtpTask)
        self.countDownResendOtpTask = null
      }
      self.countDownResendOtp = 0
      self.pin = ''
      self.telIndex = null
    },
    submitOtp () {
      var self = this
      self.isVerifying = true
      self.isExceedsLimitSmsPerDay = false
      self.isFailedSendingSms = false
      self.isExceedsLimitVerifyPerHour = false
      self.isIncorrectVerificationCode = false

      var userProfile = {}
      var uuid = JSON.parse(self.getPromptPaiCookies('Uuid'))
      var isAuthenticated = JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
      if (isAuthenticated) {
        userProfile = JSON.parse(self.getPromptPaiCookies('UserProfile'))
        uuid = undefined
      }
      self.pp.verifyPhoneNo(
        'name' in userProfile ? userProfile.name : undefined,
        'longdousertoken' in userProfile ? userProfile.longdousertoken : undefined,
        isAuthenticated ? undefined : uuid,
        self.telList[self.telIndex].telNumber,
        self.pin,
        (result) => {
          self.pin = ''
          if (result.status.code === 200) {
            if (self.countDownResendOtpTask) {
              clearInterval(self.countDownResendOtpTask)
              self.countDownResendOtpTask = null
            }
            self.telList[self.telIndex].isVerified = true
            self.telList[self.telIndex].invalidTel = (self.telList[self.telIndex].isRequired && !self.telList[self.telIndex].isVerified)
            self.showOtpDialog = false
            self.telIndex = null
          } else if (result.status.code === 401) {
            self.loginAgain()
          } else if (result.status.code === 403) {
          } else if (result.status.code === 503) {
            if (result.status.error_code === 'overlimit_phoneno_verification') {
              self.isExceedsLimitVerifyPerHour = true
            }
            if (result.status.error_code === 'incorrect_code_verification') {
              self.isIncorrectVerificationCode = true
            }
          } else {
          }
          self.isVerifying = false
        }
      )
    },
    clickImage (index) {
      var self = this
      self.$root.$emit('openImageFullscreen', {
        src: self.imageList[index].original,
        index
      })
    },
    clickAddImage () {
      var self = this
      if (self.util.getUserAgentMeta() === 'iOS application' || self.util.getUserAgentMeta() === 'Android application') {
        window.selectImageFile = (ref, base64List) => {
          for (var i = 0; i < base64List.length; i++) {
            base64List[i] = `data:image/png;base64,${base64List[i]}`
          }
          self.manageImageListForNativeApp(base64List)
        }
        window.blockSelectImageFile = () => {
          console.log('blockSelectImageFile')
        }
        window.location.href = 'https://native.selectImageFile?ref=createplaceform&multiple=1'
      } else {
        self.$refs[`add-image-input`].click()
      }
    },
    manageImageList () {
      var self = this
      var imageFileList = self.$refs[`add-image-input`].files

      var loadImage = (index) => {
        return (e) => {
          self.util.resizeImage(e.target.result, 2048, 2048, self.imageList[index].orientation, (originalUrl) => {
            self.imageList[index].original = originalUrl
          })
          self.util.resizeImage(e.target.result, 640, 640, self.imageList[index].orientation, (previewUrl) => {
            self.imageList[index].preview = previewUrl
          })
        }
      }

      var orientationCallback = function (index) {
        return (orientation) => {
          self.imageList[index].orientation = orientation
          var reader = new FileReader()
          reader.onload = loadImage(index)
          reader.readAsDataURL(self.imageList[index].file)
        }
      }

      for (var i = 0; i < imageFileList.length; i++) {
        if (imageFileList[i]) {
          self.imageList.push({
            file: imageFileList[i],
            original: '',
            preview: '',
            comment: '',
            orientation: -1
          })
          // self.util.getOrientation(
          //   self.imageList[self.imageList.length - 1].file,
          //   orientationCallback(self.imageList.length - 1)
          // )
          orientationCallback(self.imageList.length - 1)(self.imageList[self.imageList.length - 1].orientation)
        }
      }

      self.isShowAddPlaceImageForm = true
      self.$refs[`add-image-input`].value = ''
    },
    manageImageListForNativeApp (base64List) {
      var self = this

      var loadOriginalImage = (index) => {
        return (url) => {
          self.imageList[index].file = url
          self.imageList[index].original = url
        }
      }

      var loadImage = (index) => {
        return (previewUrl) => {
          self.imageList[index].preview = previewUrl
        }
      }

      for (var i = 0; i < base64List.length; i++) {
        if (base64List[i]) {
          self.imageList.push({
            // file: self.util.createFileFromUrl(base64List[i]),
            file: base64List[i],
            original: base64List[i],
            preview: '',
            comment: '',
            orientation: -1
          })
          self.util.resizeImage(
            self.imageList[self.imageList.length - 1].original,
            2048,
            2048,
            self.imageList[self.imageList.length - 1].orientation,
            loadOriginalImage(self.imageList.length - 1)
          )
          self.util.resizeImage(
            self.imageList[self.imageList.length - 1].original,
            640,
            640,
            self.imageList[self.imageList.length - 1].orientation,
            loadImage(self.imageList.length - 1)
          )
        }
      }

      self.isShowAddPlaceImageForm = true
      self.$refs[`add-image-input`].value = ''
    },
    rotate (index) {
      var self = this
      self.util.resizeImage(self.imageList[index].original, -1, -1, 6, (originalUrl) => {
        self.imageList[index].original = originalUrl
        self.clickImage(index)
      })
      self.util.resizeImage(self.imageList[index].original, 640, 640, 6, (previewUrl) => {
        self.imageList[index].preview = previewUrl
      })
    },
    hFlip (index) {
      var self = this
      self.util.resizeImage(self.imageList[index].original, -1, -1, 2, (originalUrl) => {
        self.imageList[index].original = originalUrl
        self.clickImage(index)
      })
      self.util.resizeImage(self.imageList[index].original, 640, 640, 2, (previewUrl) => {
        self.imageList[index].preview = previewUrl
      })
    },
    vFlip (index) {
      var self = this
      self.util.resizeImage(self.imageList[index].original, -1, -1, 4, (originalUrl) => {
        self.imageList[index].original = originalUrl
        self.clickImage(index)
      })
      self.util.resizeImage(self.imageList[index].original, 640, 640, 4, (previewUrl) => {
        self.imageList[index].preview = previewUrl
      })
    },
    deleteImage (index) {
      var self = this
      self.imageList.splice(index, 1)
    },
    validate () {
      var self = this
      var isValid = true

      // https://mmtech.slack.com/archives/C8PCNMN1J/p1624617691042900
      // if (window.longdoPdpa) {
      //   if (!window.longdoPdpa.isUserAllowed()) {
      //     const el = document.querySelector('#pdpa-longdocom-div')
      //     if (el) {
      //       window.longdo.Util.addClass(el, 'shake-center')
      //       setTimeout(() => {
      //         window.longdo.Util.removeClass(el, 'shake-center')
      //       }, 800)
      //     }
      //     isValid = false
      //   }
      // }

      if (self.category.id === '') {
        self.$nextTick(() => {
          const el = self.$refs['place-category']
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
            }, 800)
          }
        })
        isValid = false
      }

      if (self.invalidPrimaryName) {
        self.$nextTick(() => {
          const el = document.querySelector('#primary-name')
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
              self.primaryName = ''
            }, 800)
          }
        })
        isValid = false
      }

      if (self.invalidSecondaryName) {
        self.$nextTick(() => {
          const el = document.querySelector('#secondary-name')
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
              self.secondaryName = ''
            }, 800)
          }
        })
        isValid = false
      }

      if (self.telList.length > 1) {
        self.telList = self.telList.filter(t => t.telNumber !== '')
        if (self.telList.length === 0) {
          self.addTel()
        }
      }
      self.telList.forEach(t => {
        t.invalidTel = (t.isRequired && !t.isVerified)
      })
      if (self.telList.filter(t => t.invalidTel).length > 0) {
        self.showMoreDetail = true
        isValid = false
      }
      self.$nextTick(() => {
        self.telList.forEach((t, index) => {
          if (t.invalidTel) {
            const el = self.$refs[`tel-${index}`]
            if (el[0]) {
              el[0].scrollIntoView(true)
              window.longdo.Util.addClass(el[0], 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el[0], 'shake')
              }, 800)
            }
          }
        })
      })

      if (self.isThailand) {
        if (self.invalidPrimaryVillage) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-village')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondaryVillage) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-village')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimaryCondo) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-condo')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondaryCondo) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-condo')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimaryBuilding) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-building')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondaryBuilding) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-building')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimarySoi) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-soi')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondarySoi) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-soi')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimaryRoad) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-road')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondaryRoad) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-road')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimarySubdistrict) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-subdistrict')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondarySubdistrict) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-subdistrict')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimaryDistrict) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-district')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondaryDistrict) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-district')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidPrimaryProvince) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#primary-province')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = true
          self.showMoreDetail = true
          isValid = false
        }

        if (self.invalidSecondaryProvince) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#secondary-province')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.isPrimaryMode = false
          self.showMoreDetail = true
          isValid = false
        }

        if (!(/^\d{5}?$/g.test(self.postcode))) {
          self.$nextTick(() => {
            const el = window.document.querySelector('#postcode')
            if (el) {
              window.longdo.Util.addClass(el, 'shake')
              setTimeout(() => {
                window.longdo.Util.removeClass(el, 'shake')
              }, 800)
            }
          })
          self.showMoreDetail = true
          isValid = false
        }
      }

      if (self.invalidPrimaryAddressDetail) {
        self.$nextTick(() => {
          const el = window.document.querySelector('#primary-address-detail')
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
            }, 800)
          }
        })
        self.isPrimaryMode = true
        self.showMoreDetail = true
        isValid = false
      }

      if (self.invalidSecondaryAddressDetail) {
        self.$nextTick(() => {
          const el = window.document.querySelector('#secondary-address-detail')
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
            }, 800)
          }
        })
        self.isPrimaryMode = false
        self.showMoreDetail = true
        isValid = false
      }

      if (self.invalidPrimaryCountry) {
        self.$nextTick(() => {
          const el = window.document.querySelector('#primary-country')
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
            }, 800)
          }
        })
        self.isPrimaryMode = true
        self.showMoreDetail = true
        isValid = false
      }

      if (self.invalidSecondaryCountry) {
        self.$nextTick(() => {
          const el = window.document.querySelector('#secondary-country')
          if (el) {
            window.longdo.Util.addClass(el, 'shake')
            setTimeout(() => {
              window.longdo.Util.removeClass(el, 'shake')
            }, 800)
          }
        })
        self.isPrimaryMode = false
        self.showMoreDetail = true
        isValid = false
      }

      return isValid
    },
    clickCreateButton () {
      var self = this
      self.hasFirstClickCreateButton = true
      if (self.$i18n.locale === 'th' && !(/[\u0E00-\u0E7F']/.test(self.primaryName)) && self.secondaryName === '') {
        self.secondaryName = self.primaryName
      }
      if (self.$i18n.locale === 'en' && !(/[\u0E00-\u0E7F']/.test(self.secondaryName)) && self.primaryName === '') {
        self.primaryName = self.secondaryName
      }

      if (self.$i18n.locale === 'th') {
        if (
          self.primarySoi !== '' &&
          self.primarySoi.indexOf('ซ.') !== 0 &&
          self.primarySoi.indexOf('ซอย') !== 0
        ) {
          self.primarySoi = `ซอย${self.primarySoi}`
        }
        if (self.secondarySoi !== '' && self.secondarySoi.indexOf('Soi') !== 0) {
          self.secondarySoi = `Soi ${self.secondarySoi}`
        }
        if (
          self.primaryRoad !== '' &&
          self.primaryRoad.indexOf('ถ.') !== 0 &&
          self.primaryRoad.indexOf('ถนน') !== 0
        ) {
          self.primaryRoad = `ถนน${self.primaryRoad}`
        }
        if (
          self.secondaryRoad !== '' &&
          self.secondaryRoad.indexOf('Road') !== self.secondaryRoad.length - 4 &&
          self.secondaryRoad.indexOf('Rd.') !== self.secondaryRoad.length - 3
        ) {
          self.secondaryRoad = `${self.secondaryRoad} Rd.`
        }
      } else {
        if (self.primarySoi !== '' && self.primarySoi.indexOf('Soi') !== 0) {
          self.primarySoi = `Soi ${self.primarySoi}`
        }
        if (
          self.secondarySoi !== '' &&
          self.secondarySoi.indexOf('ซ.') !== 0 &&
          self.secondarySoi.indexOf('ซอย') !== 0
        ) {
          self.secondarySoi = `ซอย${self.secondarySoi}`
        }
        if (
          self.primaryRoad !== '' &&
          self.primaryRoad.indexOf('Road') !== self.primaryRoad.length - 3 &&
          self.primaryRoad.indexOf('Rd.') !== self.primaryRoad.length - 3
        ) {
          self.primaryRoad = `${self.primaryRoad} Rd.`
        }
        if (
          self.secondaryRoad !== '' &&
          self.secondaryRoad.indexOf('ถ.') !== 0 &&
          self.secondaryRoad.indexOf('ถนน') !== 0
        ) {
          self.secondaryRoad = `ถนน${self.secondaryRoad}`
        }
      }

      if (self.validate()) {
        self.step = 2
        self.isLockLocation = true
      }
    },
    clickBackButton () {
      var self = this
      self.step = 1
    },
    clickConfirmButton () {
      var self = this
      if (self.isSaving) {
        return
      }
      self.isSaving = true

      var userProfile = {}
      var uuid = JSON.parse(self.getPromptPaiCookies('Uuid'))
      var isAuthenticated = JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
      if (isAuthenticated) {
        userProfile = JSON.parse(self.getPromptPaiCookies('UserProfile'))
        uuid = undefined
      }
      var location = self.myPin.location()

      var data = {
        uuid: isAuthenticated ? undefined : uuid,
        username: 'name' in userProfile ? userProfile.name : undefined,
        ldtoken: 'longdousertoken' in userProfile ? userProfile.longdousertoken : undefined,
        nameTh: self.$root.$i18n.locale === 'th' ? (self.category.id !== '-1' ? self.primaryName : (self.houseNo || 'บ้าน / ที่อยู่อาศัย')) : (self.category.id !== '-1' ? self.secondaryName : (self.houseNo || 'House / Residence')),
        nameEn: self.$root.$i18n.locale === 'en' ? (self.category.id !== '-1' ? self.primaryName : (self.houseNo || 'บ้าน / ที่อยู่อาศัย')) : (self.category.id !== '-1' ? self.secondaryName : (self.houseNo || 'House / Residence')),
        telephoneList: self.telList.map(t => t.telNumber !== '').length > 0 ? self.telList.map(t => t.telNumber) : [],
        categoryId: self.category.id,
        latitude: location.lat,
        longitude: location.lon,
        houseNo: self.isThailand ? (self.houseNo || '') : '',
        moo: self.isThailand ? (self.moo || '') : '',
        parentPlaceId: self.isThailand ? (self.refPlace.value || '') : '',
        floor: self.isThailand ? (self.floor || '') : '',
        room: self.isThailand ? (self.roomNo || '') : '',
        villageTh: self.isThailand ? ((self.$root.$i18n.locale === 'th' ? self.primaryVillage : self.secondaryVillage) || '') : '',
        villageEn: self.isThailand ? ((self.$root.$i18n.locale === 'en' ? self.primaryVillage : self.secondaryVillage) || '') : '',
        condoTh: self.isThailand ? ((self.$root.$i18n.locale === 'th' ? self.primaryCondo : self.secondaryCondo) || '') : '',
        condoEn: self.isThailand ? ((self.$root.$i18n.locale === 'en' ? self.primaryCondo : self.secondaryCondo) || '') : '',
        buildingTh: self.isThailand ? ((self.$root.$i18n.locale === 'th' ? self.primaryBuilding : self.secondaryBuilding) || '') : '',
        buildingEn: self.isThailand ? ((self.$root.$i18n.locale === 'en' ? self.primaryBuilding : self.secondaryBuilding) || '') : '',
        alleyTh: self.isThailand ? ((self.$root.$i18n.locale === 'th' ? self.primarySoi : self.secondarySoi) || '') : '',
        alleyEn: self.isThailand ? ((self.$root.$i18n.locale === 'en' ? self.primarySoi : self.secondarySoi) || '') : '',
        roadTh: self.isThailand ? ((self.$root.$i18n.locale === 'th' ? self.primaryRoad : self.secondaryRoad) || '') : '',
        roadEn: self.isThailand ? ((self.$root.$i18n.locale === 'en' ? self.primaryRoad : self.secondaryRoad) || '') : '',
        subdistrictTh: (self.$root.$i18n.locale === 'th' ? self.primarySubdistrict : self.secondarySubdistrict) || '',
        subdistrictEn: (self.$root.$i18n.locale === 'en' ? self.primarySubdistrict : self.secondarySubdistrict) || '',
        districtTh: (self.$root.$i18n.locale === 'th' ? self.primaryDistrict : self.secondaryDistrict) || '',
        districtEn: (self.$root.$i18n.locale === 'en' ? self.primaryDistrict : self.secondaryDistrict) || '',
        provinceTh: (self.$root.$i18n.locale === 'th' ? self.primaryProvince : self.secondaryProvince) || '',
        provinceEn: (self.$root.$i18n.locale === 'en' ? self.primaryProvince : self.secondaryProvince) || '',
        countryTh: (self.$root.$i18n.locale === 'th' ? self.primaryCountry : self.secondaryCountry) || '',
        countryEn: (self.$root.$i18n.locale === 'en' ? self.primaryCountry : self.secondaryCountry) || '',
        postcode: self.postcode || '',
        addressDetailTh: (self.$root.$i18n.locale === 'th' ? self.primaryAddressDetail : self.secondaryAddressDetail) || '',
        addressDetailEn: (self.$root.$i18n.locale === 'en' ? self.primaryAddressDetail : self.secondaryAddressDetail) || '',
        geocode: self.geocode,
        telephonePpid: self.telList.filter(t => t.selected).length > 0 ? self.telList.filter(t => t.selected)[0].telNumber : undefined
      }

      if (self.mode === 'CREATE') {
        data.imageList = self.imageList.map(i => { return i.original })
        data.imageCommentList = self.imageList.map(i => { return i.comment })
        self.pp.createNewPromptpai(
          data,
          undefined,
          (result) => {
            if (result.status.code === 200) {
              self.updateRecentList()
              self.updateFavoriteList()
              self.isSaving = false
              // self.$root.$emit('toggleCreateMode', true, true)
              self.isCompleted = true
              window.gtag('event', 'create', {'event_category': 'engagement', 'event_label': `create_place`})
              setTimeout(() => {
                self.isCompleted = false
                // self.$root.$emit('closeModal')
                self.$router.replace(`/p/${result.data.promptpai_id}`)
                self.$root.$emit('askForTransfer')
              }, 1000)
            } else if (result.status.code === 401) {
              self.isSaving = false
              self.loginAgain()
            } else if (result.status.code === 403) {
              self.isSaving = false
            } else {
              self.isSaving = false
            }
          }
        )
      } else if (self.mode === 'EDIT') {
        data.promptpaiId = self.promptpaiDescription.promptpai_id
        self.pp.updatePromptpai(
          data,
          undefined,
          (result) => {
            if (result.status.code === 200) {
              self.updateRecentList()
              self.updateFavoriteList()
              self.isSaving = false
              self.isCompleted = true
              window.gtag('event', 'update', {'event_category': 'engagement', 'event_label': `update_place_${result.data.promptpai_id}`})
              setTimeout(() => {
                self.isCompleted = false
                self.$root.$emit('updatePlaceCard')
                self.$root.$emit('closeModal')
                self.$root.$emit('askForTransfer')
              }, 1000)
            } else if (result.status.code === 401) {
              self.isSaving = false
              self.loginAgain()
            } else if (result.status.code === 403) {
              self.isSaving = false
            } else {
              self.isSaving = false
            }
          }
        )
      }
    },
    initialAddress (location) {
      var self = this
      self.pp.reverseGeocoding(
        undefined,
        location.lat,
        location.lon,
        self.$i18n.locale,
        (result) => {
          if (result !== null) {
            if (result.road) {
              const indexOfSoi = result.road.indexOf(self.$i18n.locale === 'th' ? 'ซอย' : 'Soi')
              const indexOfSoiWithParentheses = result.road.indexOf(self.$i18n.locale === 'th' ? '(ซอย' : '(Soi')
              const foundRoadAndSoi = indexOfSoi > 0 && indexOfSoiWithParentheses >= 0
              if (foundRoadAndSoi) {
                self.primarySoi = result.road.substring(indexOfSoiWithParentheses + 1).replaceAll(/\)/g, '')
                self.primaryRoad = result.road.substring(0, indexOfSoiWithParentheses)
              }
              const foundSoi = indexOfSoi === 0
              if (foundSoi) {
                self.primarySoi = result.road
              }
              const foundRoad = indexOfSoi < 0
              if (foundRoad) {
                self.primaryRoad = result.road
              }
            }
            // const street = result.road || ''
            // if (street.indexOf('Soi') >= 0 || street.indexOf('ซอย') >= 0) {
            //   self.primarySoi = street
            // } else {
            //   self.primaryRoad = street
            // }
            self.primarySubdistrict = result.subdistrict || ''
            self.primaryDistrict = result.district || ''
            self.primaryProvince = result.province || ''
            self.primaryCountry = (result.country || '').replace('ประเทศไทย', 'ไทย')
            self.postcode = result.postcode || ''
            self.geocode = result.geocode || ''
          }
        }
      )
      self.pp.reverseGeocoding(
        undefined,
        location.lat,
        location.lon,
        self.$i18n.locale === 'th' ? 'en' : 'th',
        (result) => {
          if (result !== null) {
            if (result.road) {
              const indexOfSoi = result.road.indexOf(self.$i18n.locale === 'th' ? 'Soi' : 'ซอย')
              const indexOfSoiWithParentheses = result.road.indexOf(self.$i18n.locale === 'th' ? '(Soi' : '(ซอย')
              const foundRoadAndSoi = indexOfSoi > 0 && indexOfSoiWithParentheses >= 0
              if (foundRoadAndSoi) {
                self.secondarySoi = result.road.substring(indexOfSoiWithParentheses + 1).replaceAll(/\)/g, '')
                self.secondaryRoad = result.road.substring(0, indexOfSoiWithParentheses)
              }
              const foundSoi = indexOfSoi === 0
              if (foundSoi) {
                self.secondarySoi = result.road
              }
              const foundRoad = indexOfSoi < 0
              if (foundRoad) {
                self.secondaryRoad = result.road
              }
            }
            // const street = result.road || ''
            // if (street.indexOf('Soi') >= 0 || street.indexOf('ซอย') >= 0) {
            //   self.secondarySoi = street
            // } else {
            //   self.secondaryRoad = street
            // }
            self.secondarySubdistrict = result.subdistrict || ''
            self.secondaryDistrict = result.district || ''
            self.secondaryProvince = result.province || ''
            self.secondaryCountry = (result.country || '').replace('ประเทศไทย', 'ไทย')
            self.postcode = result.postcode || ''
            self.geocode = result.geocode || ''
          }
        }
      )
    },
    updateRecentList () {
      var self = this
      var userProfile = {}
      var uuid = JSON.parse(self.getPromptPaiCookies('Uuid'))
      var isAuthenticated = JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
      if (isAuthenticated) {
        userProfile = JSON.parse(self.getPromptPaiCookies('UserProfile'))
        uuid = undefined
      }
      self.setRecentPage(1)
      self.pp.getRecentPlaces(
        'name' in userProfile ? userProfile.name : undefined,
        'longdousertoken' in userProfile ? userProfile.longdousertoken : undefined,
        uuid,
        self.itemsPerPage,
        'all',
        self.itemsPerPage * (self.recentPage - 1),
        undefined,
        (result) => {
          if (result.status.code === 200) {
            self.setRecentList(result.data)
          } else if (result.status.code === 401) {
            self.loginAgain()
          } else if (result.status.code === 403) {
          } else {
          }
        }
      )
    },
    updateFavoriteList () {
      var self = this
      var userProfile = {}
      var uuid = JSON.parse(self.getPromptPaiCookies('Uuid'))
      var isAuthenticated = JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
      if (isAuthenticated) {
        userProfile = JSON.parse(self.getPromptPaiCookies('UserProfile'))
        uuid = undefined
      }
      self.setFavoritePage(1)
      self.pp.getFavoritePlaces(
        'name' in userProfile ? userProfile.name : undefined,
        'longdousertoken' in userProfile ? userProfile.longdousertoken : undefined,
        isAuthenticated ? undefined : uuid,
        self.itemsPerPage,
        self.itemsPerPage * (self.favoritePage - 1),
        undefined,
        (result) => {
          if (result.status.code === 200) {
            self.setFavoriteList(result.data)
          } else if (result.status.code === 401) {
            self.loginAgain()
          } else if (result.status.code === 403) {
          } else {
          }
        }
      )
    },
    animateTelGuide () {
      var self = this
      self.showTelGuide = true
      setTimeout(() => {
        self.showTelGuide = false
      }, 8000)
    },
    checkPhoneNoVerificationStatus (index) {
      var self = this
      var userProfile = {}
      var uuid = JSON.parse(self.getPromptPaiCookies('Uuid'))
      var isAuthenticated = JSON.parse(self.getPromptPaiCookies('isAuthenticated'))
      if (isAuthenticated) {
        userProfile = JSON.parse(self.getPromptPaiCookies('UserProfile'))
        uuid = undefined
      }
      self.pp.getPhoneNoVerificationStatus(
        'name' in userProfile ? userProfile.name : undefined,
        'longdousertoken' in userProfile ? userProfile.longdousertoken : undefined,
        isAuthenticated ? undefined : uuid,
        self.telList[index].telNumber,
        (result) => {
          if (result.status.code === 200) {
            self.telList[index].isVerified = result.data.verified
            self.telList[index].isRequired = result.data.required
            self.telList[index].isAvailable = 'pp_id_available' in result.data ? result.data.pp_id_available : true
            if (self.mode === 'EDIT') {
              self.telList[index].isAvailable = self.telList[index].isAvailable || (self.promptpaiDescription.promptpai_id === result.data.pp_id)
            }
            self.$nextTick(() => {
              const el = self.$refs[`tel-${index}-require-verification`]
              if (el) {
                window.longdo.Util.addClass(el[0], 'shake')
                setTimeout(() => {
                  window.longdo.Util.removeClass(el[0], 'shake')
                }, 800)
              }
            })
          } else if (result.status.code === 401) {
            self.loginAgain()
          } else if (result.status.code === 403) {
          } else {
          }
        }
      )
    },
    getSubdistrictSuggestList (callback) {
      var self = this
      var location = self.myPin.location()
      var keyword = self.isPrimaryMode ? self.primarySubdistrict : self.secondarySubdistrict

      if (self.suggestSubdistrictTask) {
        clearTimeout(self.suggestSubdistrictTask)
      }
      self.suggestSubdistrictTask = setTimeout(() => {
        self.pp.longdoMapSearch(
          {
            locale: self.$i18n.locale,
            lat: location.lat,
            lon: location.lon,
            keyword: keyword.length < 2 ? undefined : keyword,
            tag: 'subdistrict',
            dataset: 'data2a',
            area: self.districtCode || self.provinceCode || ''
          },
          (result) => {
            self.primarySubdistrictList = result.data

            self.pp.longdoMapSearch(
              {
                locale: self.$i18n.locale === 'th' ? 'en' : 'th',
                lat: location.lat,
                lon: location.lon,
                keyword: keyword.length < 2 ? undefined : keyword,
                tag: 'subdistrict',
                dataset: 'data2a',
                area: self.districtCode || self.provinceCode || ''
              },
              (result) => {
                self.secondarySubdistrictList = result.data

                if (callback) {
                  callback()
                }
              }
            )
          }
        )
      }, 300)
    },
    onSelectPrimarySubdistrictSuggest (value) {
      var self = this
      var spliter = self.$i18n.locale === 'th' ? ' ' : ', '
      var splitedValue = value.split(spliter)
      if (splitedValue.length > 0) {
        const index = self.primarySubdistrictList.map(p => p.name).indexOf(value)
        if (index >= 0) {
          self.subdistrictCode = self.primarySubdistrictList[index].address.replace(/รหัส: /g, '').replace(/Code: /g, '')
          self.primarySubdistrictList[index].tag.forEach(t => {
            if (t.indexOf('__POST') >= 0) {
              self.postcode = t.replace('__POST', '')
            }
          })
        }
        self.primarySubdistrict = splitedValue[0]
      }
      if (splitedValue.length > 1) {
        self.primaryDistrict = splitedValue[1]
      }
      if (splitedValue.length > 2) {
        self.primaryProvince = splitedValue[2]
      }
    },
    onSelectSecondarySubdistrictSuggest (value) {
      var self = this
      var spliter = self.$i18n.locale === 'th' ? ', ' : ' '
      var splitedValue = value.split(spliter)
      if (splitedValue.length > 0) {
        const index = self.secondarySubdistrictList.map(p => p.name).indexOf(value)
        if (index >= 0) {
          self.subdistrictCode = self.secondarySubdistrictList[index].address.replace(/รหัส: /g, '').replace(/Code: /g, '')
          self.secondarySubdistrictList[index].tag.forEach(t => {
            if (t.indexOf('__POST') >= 0) {
              self.postcode = t.replace('__POST', '')
            }
          })
        }
        self.secondarySubdistrict = splitedValue[0]
      }
      if (splitedValue.length > 1) {
        self.secondaryDistrict = splitedValue[1]
      }
      if (splitedValue.length > 2) {
        self.secondaryProvince = splitedValue[2]
      }
    },
    getDistrictSuggestList (callback) {
      var self = this
      var location = self.myPin.location()
      var keyword = self.isPrimaryMode ? self.primaryDistrict : self.secondaryDistrict

      if (self.suggestDistrictTask) {
        clearTimeout(self.suggestDistrictTask)
      }
      self.suggestDistrictTask = setTimeout(() => {
        self.pp.longdoMapSearch(
          {
            locale: self.$i18n.locale,
            lat: location.lat,
            lon: location.lon,
            keyword: keyword.length < 2 ? undefined : keyword,
            tag: 'district',
            dataset: 'data2a',
            area: self.provinceCode || ''
          },
          (result) => {
            self.primaryDistrictList = result.data

            self.pp.longdoMapSearch(
              {
                locale: self.$i18n.locale === 'th' ? 'en' : 'th',
                lat: location.lat,
                lon: location.lon,
                keyword: keyword.length < 2 ? undefined : keyword,
                tag: 'district',
                dataset: 'data2a',
                area: self.provinceCode || ''
              },
              (result) => {
                self.secondaryDistrictList = result.data

                if (callback) {
                  callback()
                }
              }
            )
          }
        )
      }, 300)
    },
    onSelectPrimaryDistrictSuggest (value) {
      var self = this
      var spliter = self.$i18n.locale === 'th' ? ' ' : ', '
      var splitedValue = value.split(spliter)
      if (splitedValue.length > 0) {
        const index = self.primaryDistrictList.map(p => p.name).indexOf(value)
        if (index >= 0) {
          self.districtCode = self.primaryDistrictList[index].address.replace(/รหัส: /g, '').replace(/Code: /g, '')
        }
        self.primaryDistrict = splitedValue[0]

        self.postcode = ''
        self.primarySubdistrict = ''
        self.secondarySubdistrict = ''

        self.$nextTick(() => {
          self.getSubdistrictSuggestList()
        })
      }
      if (splitedValue.length > 1) {
        self.primaryProvince = splitedValue[1]
      }
    },
    onSelectSecondaryDistrictSuggest (value) {
      var self = this
      var spliter = self.$i18n.locale === 'th' ? ', ' : ' '
      var splitedValue = value.split(spliter)
      if (splitedValue.length > 0) {
        const index = self.secondaryDistrictList.map(p => p.name).indexOf(value)
        if (index >= 0) {
          self.districtCode = self.secondaryDistrictList[index].address.replace(/รหัส: /g, '').replace(/Code: /g, '')
        }
        self.secondaryDistrict = splitedValue[0]

        self.postcode = ''
        self.primarySubdistrict = ''
        self.secondarySubdistrict = ''

        self.$nextTick(() => {
          self.getSubdistrictSuggestList()
        })
      }
      if (splitedValue.length > 1) {
        self.secondaryProvince = splitedValue[1]
      }
    },
    getProvinceSuggestList (callback) {
      var self = this
      var location = self.myPin.location()
      var keyword = self.isPrimaryMode ? self.primaryProvince : self.secondaryProvince
      if (self.suggestProvinceTask) {
        clearTimeout(self.suggestProvinceTask)
      }
      self.suggestProvinceTask = setTimeout(() => {
        self.pp.longdoMapSearch(
          {
            locale: self.$i18n.locale,
            lat: location.lat,
            lon: location.lon,
            keyword: keyword.length < 2 ? undefined : keyword,
            tag: 'province',
            dataset: 'data2a',
            limit: 100
          },
          (result) => {
            self.primaryProvinceList = result.data

            self.pp.longdoMapSearch(
              {
                locale: self.$i18n.locale === 'th' ? 'en' : 'th',
                lat: location.lat,
                lon: location.lon,
                keyword: keyword.length < 2 ? undefined : keyword,
                tag: 'province',
                dataset: 'data2a',
                limit: 100
              },
              (result) => {
                self.secondaryProvinceList = result.data

                if (callback) {
                  callback()
                }
              }
            )
          }
        )
      }, 300)
    },
    onSelectPrimaryProvinceSuggest (value) {
      var self = this
      var spliter = self.$i18n.locale === 'th' ? ' ' : ', '
      var splitedValue = value.split(spliter)
      if (splitedValue.length > 0) {
        const index = self.primaryProvinceList.map(p => p.name).indexOf(value)
        if (index >= 0) {
          self.provinceCode = self.primaryProvinceList[index].address.replace(/รหัส: /g, '').replace(/Code: /g, '')
        }
        self.primaryProvince = splitedValue[0]

        self.postcode = ''
        self.primarySubdistrict = ''
        self.primaryDistrict = ''
        self.secondarySubdistrict = ''
        self.secondaryDistrict = ''

        self.$nextTick(() => {
          self.getDistrictSuggestList()
          self.getSubdistrictSuggestList()
        })
      }
    },
    onSelectSecondaryProvinceSuggest (value) {
      var self = this
      var spliter = self.$i18n.locale === 'th' ? ', ' : ' '
      var splitedValue = value.split(spliter)
      if (splitedValue.length > 0) {
        const index = self.secondaryProvinceList.map(p => p.name).indexOf(value)
        if (index >= 0) {
          self.provinceCode = self.secondaryProvinceList[index].address.replace(/รหัส: /g, '').replace(/Code: /g, '')
        }
        self.secondaryProvince = splitedValue[0]

        self.postcode = ''
        self.primarySubdistrict = ''
        self.primaryDistrict = ''
        self.secondarySubdistrict = ''
        self.secondaryDistrict = ''

        self.$nextTick(() => {
          self.getDistrictSuggestList()
          self.getSubdistrictSuggestList()
        })
      }
    },
    getRefPlaceSuggestList (location, id) {
      var self = this
      if (!location) {
        location = self.myPin.location()
      }
      self.pp.getParentPlaceList(
        location.lat,
        location.lon,
        self.mode === 'CREATE' ? undefined : `A${self.promptpaiDescription.promptpai_id}`,
        (result) => {
          self.refPlaceList = result.data.map(i => {
            return {
              value: i.id,
              label: i.name_th,
              labelEn: i.name_en
            }
          })
          self.$nextTick(() => {
            self.refPlace = {
              value: self.refPlaceList.length > 0 ? (id || '') : ''
            }
          })
        }
      )
    },
    onSelectPrimaryCountrySuggest (value) {
      var self = this
      self.primaryCountry = value
      const index = self.countryList.map(p => p[`name${self.$i18n.locale === 'th' ? '' : 'En'}`]).indexOf(value)
      if (index >= 0) {
        self.secondaryCountry = self.countryList[index][`name${self.$i18n.locale === 'th' ? 'En' : ''}`]
      }
    },
    onSelectSecondaryCountrySuggest (value) {
      var self = this
      self.secondaryCountry = value
      const index = self.countryList.map(p => p[`name${self.$i18n.locale === 'th' ? 'En' : ''}`]).indexOf(value)
      if (index >= 0) {
        self.primaryCountry = self.countryList[index][`name${self.$i18n.locale === 'th' ? '' : 'En'}`]
      }
    }
  },
  watch: {
    step (newStep, oldStep) {
      var self = this
      self.$nextTick(() => {
        var option = {
          height: `calc(100% - ${self.$refs['checkDetail'].clientHeight}px)`
        }
        self.mapStyle = self.step === 2 ? option : {}

        if (self.countDownConfirmTask) {
          clearInterval(self.countDownConfirmTask)
        }

        if (self.step === 2) {
          self.countDownConfirm = 5
          self.countDownConfirmTask = setInterval(() => {
            self.countDownConfirm--
            if (self.countDownConfirm === -1) {
              clearInterval(self.countDownConfirmTask)
            }
          }, 1000)
        } else {
          self.countDownConfirm = 0
        }
      })
    },
    mapStyle () {
      var self = this
      self.$nextTick(() => {
        self.createPlaceMap.resize()
      })
    },
    isLockLocation () {
      var self = this
      self.$nextTick(() => {
        if (!self.isLockLocation) {
          self.createPlaceMap.location(self.createPlaceMap.Overlays.list()[0].location())
          self.createPlaceMap.Overlays.clear()
        } else {
          var pinOption = {
            icon: {
              url: self.util.getImagePath('my_place_pin_blue.png'),
              urlHD: self.util.getImagePath('my_place_pin_blue@2x.png'),
              offset: {
                x: 27 / 2,
                y: 39
              }
            }
          }
          self.myPin = new window.longdo.Marker(self.createPlaceMap.location(), pinOption)
          self.createPlaceMap.Overlays.add(self.myPin)
        }
      })
    },
    'category.id' () {
      var self = this
      self.$nextTick(() => {
        if (self.mode === 'CREATE' && self.category.id !== '-1') {
          if (!self.isEditedPrimaryName) {
            self.primaryName = ''
          }
          if (!self.isEditedSecondaryName) {
            self.secondaryName = ''
          }
          self.showMoreDetail = true
        }
      })
    },
    showMoreDetail () {
      var self = this
      self.$nextTick(() => {
        if (self.showMoreDetail) {
          self.animateTelGuide()
        }
      })
    },
    primaryName () {
      var self = this
      self.$nextTick(() => {
        self.isEditedPrimaryName = true
        self.hasFirstChangePrimaryName = true
      })
    },
    secondaryName () {
      var self = this
      self.$nextTick(() => {
        self.isEditedSecondaryName = true
        self.hasFirstChangeSecondaryName = true
      })
    },
    isPrimaryMode () {
      var self = this
      self.$nextTick(() => {
        self.getSubdistrictSuggestList()
        self.getDistrictSuggestList()
        self.getProvinceSuggestList()
      })
    },
    primaryVillage () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryVillage = true
      })
    },
    secondaryVillage () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryVillage = true
      })
    },
    primaryCondo () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryCondo = true
      })
    },
    secondaryCondo () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryCondo = true
      })
    },
    primaryBuilding () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryBuilding = true
      })
    },
    secondaryBuilding () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryBuilding = true
      })
    },
    primarySoi () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimarySoi = true
      })
    },
    secondarySoi () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondarySoi = true
      })
    },
    primaryRoad () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryRoad = true
      })
    },
    secondaryRoad () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryRoad = true
      })
    },
    primarySubdistrict () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimarySubdistrict = true

        if (self.primarySubdistrict === '') {
          self.primarySubdistrict = ''
          self.secondarySubdistrict = ''
          self.subdistrictCode = ''
          self.postcode = ''
        }

        if (self.primarySubdistrictList.map(s => s.name).indexOf(self.primarySubdistrict) <= 0) {
          self.getSubdistrictSuggestList(() => {
            if (self.isPrimaryMode) {
              const address = [self.primarySubdistrict, self.primaryDistrict, self.primaryProvince].join(self.$i18n.locale === 'th' ? ' ' : ', ')
              const index = self.primarySubdistrictList.map(s => s.name).indexOf(address)
              if (index >= 0) {
                var spliter = self.$i18n.locale === 'th' ? ', ' : ' '
                var splitedValue = self.secondarySubdistrictList[index].name.split(spliter)
                if (splitedValue.length > 0) {
                  self.secondarySubdistrict = splitedValue[0]
                }
                if (splitedValue.length > 1) {
                  self.secondaryDistrict = splitedValue[1]
                }
                if (splitedValue.length > 2) {
                  self.secondaryProvince = splitedValue[2]
                }
              } else {
                self.subdistrictCode = ''
              }
            }
          })
        }
      })
    },
    secondarySubdistrict () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondarySubdistrict = true

        if (self.secondarySubdistrict === '') {
          self.primarySubdistrict = ''
          self.secondarySubdistrict = ''
          self.subdistrictCode = ''
          self.postcode = ''
        }

        if (self.secondarySubdistrictList.map(s => s.name).indexOf(self.secondarySubdistrict) <= 0) {
          self.getSubdistrictSuggestList(() => {
            if (!self.isPrimaryMode) {
              const address = [self.secondarySubdistrict, self.secondaryDistrict, self.secondaryProvince].join(self.$i18n.locale === 'th' ? ', ' : ' ')
              const index = self.secondarySubdistrictList.map(s => s.name).indexOf(address)
              if (index >= 0) {
                var spliter = self.$i18n.locale === 'th' ? ' ' : ', '
                var splitedValue = self.primarySubdistrictList[index].name.split(spliter)
                if (splitedValue.length > 0) {
                  self.primarySubdistrict = splitedValue[0]
                }
                if (splitedValue.length > 1) {
                  self.primaryDistrict = splitedValue[1]
                }
                if (splitedValue.length > 2) {
                  self.primaryProvince = splitedValue[2]
                }
              } else {
                self.subdistrictCode = ''
              }
            }
          })
        }
      })
    },
    primaryDistrict () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryDistrict = true

        if (self.primaryDistrict === '') {
          self.primaryDistrict = ''
          self.secondaryDistrict = ''
          self.districtCode = ''
          self.primarySubdistrict = ''
          self.secondarySubdistrict = ''
          self.subdistrictCode = ''
          self.postcode = ''
        }

        if (self.primaryDistrictList.map(d => d.name).indexOf(self.primaryDistrict) <= 0) {
          self.getDistrictSuggestList(() => {
            if (self.isPrimaryMode) {
              const address = [self.primaryDistrict, self.primaryProvince].join(self.$i18n.locale === 'th' ? ' ' : ', ')
              const index = self.primaryDistrictList.map(d => d.name).indexOf(address)
              if (index >= 0) {
                var spliter = self.$i18n.locale === 'th' ? ', ' : ' '
                var splitedValue = self.secondaryDistrictList[index].name.split(spliter)
                if (splitedValue.length > 0) {
                  self.secondaryDistrict = splitedValue[0]
                }
                if (splitedValue.length > 1) {
                  self.secondaryProvince = splitedValue[1]
                }
              } else {
                self.districtCode = ''
              }
            }
          })
        }
      })
    },
    secondaryDistrict () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryDistrict = true

        if (self.secondaryDistrict === '') {
          self.primaryDistrict = ''
          self.secondaryDistrict = ''
          self.districtCode = ''
          self.primarySubdistrict = ''
          self.secondarySubdistrict = ''
          self.subdistrictCode = ''
          self.postcode = ''
        }

        if (self.secondaryDistrictList.map(d => d.name).indexOf(self.secondaryDistrict) <= 0) {
          self.getDistrictSuggestList(() => {
            if (!self.isPrimaryMode) {
              const address = [self.secondaryDistrict, self.secondaryProvince].join(self.$i18n.locale === 'th' ? ', ' : ' ')
              const index = self.secondaryDistrictList.map(d => d.name).indexOf(address)
              if (index >= 0) {
                var spliter = self.$i18n.locale === 'th' ? ' ' : ', '
                var splitedValue = self.primaryDistrictList[index].name.split(spliter)
                if (splitedValue.length > 0) {
                  self.primaryDistrict = splitedValue[0]
                }
                if (splitedValue.length > 1) {
                  self.primaryProvince = splitedValue[1]
                }
              } else {
                self.districtCode = ''
              }
            }
          })
        }
      })
    },
    primaryProvince () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryProvince = true

        if (self.primaryProvince === '') {
          self.primaryProvince = ''
          self.secondaryProvince = ''
          self.provinceCode = ''
          self.primaryDistrict = ''
          self.secondaryDistrict = ''
          self.districtCode = ''
          self.primarySubdistrict = ''
          self.secondarySubdistrict = ''
          self.subdistrictCode = ''
          self.postcode = ''
        }

        self.getProvinceSuggestList(() => {
          if (self.isPrimaryMode) {
            const index = self.primaryProvinceList.map(p => p.name).indexOf(self.primaryProvince)
            if (index >= 0) {
              self.secondaryProvince = self.secondaryProvinceList[index].name
            } else {
              self.provinceCode = ''
            }
          }
        })
      })
    },
    secondaryProvince () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryProvince = true

        if (self.secondaryProvince === '') {
          self.primaryProvince = ''
          self.secondaryProvince = ''
          self.provinceCode = ''
          self.primaryDistrict = ''
          self.secondaryDistrict = ''
          self.districtCode = ''
          self.primarySubdistrict = ''
          self.secondarySubdistrict = ''
          self.subdistrictCode = ''
          self.postcode = ''
        }

        self.getProvinceSuggestList(() => {
          if (!self.isPrimaryMode) {
            const index = self.secondaryProvinceList.map(p => p.name).indexOf(self.secondaryProvince)
            if (index >= 0) {
              self.primaryProvince = self.primaryProvinceList[index].name
            } else {
              self.provinceCode = ''
            }
          }
        })
      })
    },
    postcode () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePostcode = true
      })
    },
    primaryAddressDetail () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryAddressDetail = true
      })
    },
    secondaryAddressDetail () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryAddressDetail = true
      })
    },
    primaryCountry () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangePrimaryCountry = true
      })
    },
    secondaryCountry () {
      var self = this
      self.$nextTick(() => {
        self.hasFirstChangeSecondaryCountry = true
      })
    }
  },
  beforeDestroy () {
    var self = this
    window.removeEventListener('keydown', self.flagHasKeydown)
    self.$root.$off('rotate')
    self.$root.$off('hFlip')
    self.$root.$off('vFlip')
  }
}
</script>

<style scoped>
  .create-place-form {
    width: 640px;
    height: 640px;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    position: relative;
    overflow: hidden;
    margin-top: auto;
    margin-bottom: auto;
  }

  .close-confirm-container {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 15;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .close-confirm {
    height: fit-content;
    width: calc(320px - 32px);
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    overflow: hidden;
    margin-top: auto;
    margin-bottom: auto;
    background-color: rgb(255, 255, 255);
    box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.2);
  }

  .close-confirm > .body {
    padding: 16px;
    background: rgb(255, 255, 255);
    display: flex;
    flex-direction: column;
  }

  .close-confirm > .body > .text {
    margin: 16px 0px 24px 0px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    line-height: 1.75;
    text-align: center;
  }

  .close-confirm > .body > .button-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .close-confirm > .body > .button-container > button {
    width: calc((100% - 16px) / 2);
    padding: 6px;
    background: rgb(59, 144, 255);
    border-radius: 4px;
    border: none;
    color: rgb(255, 255, 255);
    margin: 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  .close-confirm > .body > .button-container > button.outline {
    background: rgb(255, 255, 255);
    border: 1px solid rgb(59, 144, 255);
    color: rgb(59, 144, 255);
  }

  .create-place-form > .body {
    height: calc(100% - 50px);
    box-sizing: border-box;
    background: rgb(255, 255, 255);
    position: relative;
  }

  .verify-phone-number {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgba(0, 0, 0, 0.5);
    width: 100%;
    height: 100%;
    z-index: 50;
  }

  .verify-phone-number .dialog {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgb(255, 255, 255);
    border-radius: 10px;
    box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.2);
    min-width: 300px;
    max-width: calc(100% - 24px);
    max-height: calc(100% - 24px);
    padding: 16px;
    box-sizing: border-box;
    text-align: center;
    display: flex;
    flex-direction: column;
  }

  .verify-phone-number .dialog .title {
    color: rgb(34, 84, 156);
    font-family: Prompt;
    font-weight: bold;
    font-size: 18px;
  }

  .verify-phone-number .dialog input[type="text"] {
    border: 2px solid rgb(215, 215, 215);
    border-radius: 4px;
    width: 120px;
    font-size: 36px;
    font-weight: bold;
    text-align: center;
    margin: 24px auto;
    transition: border-color 0.3s ease 0s;
  }

  .verify-phone-number .dialog input[type="text"]:focus {
    border: 2px solid rgb(34, 84, 156);
  }

  .verify-phone-number .dialog .guide,
  .verify-phone-number .dialog .guide * {
    color: rgb(120, 120, 120);
    font-size: 14px;
  }

  .verify-phone-number .dialog .guide.error,
  .verify-phone-number .dialog .guide.error * {
    color: rgb(220, 20, 60);
    margin-bottom: 16px;
  }

  .verify-phone-number .dialog .resend {
    width: fit-content;
    margin: 24px auto;
    border: none;
    background-color: transparent;
    color: rgb(34, 84, 156);
    cursor: pointer;
    font-weight: bold;
    font-family: Prompt;
  }

  .verify-phone-number .dialog .resend:disabled {
    cursor: default;
  }

  .verify-phone-number .dialog .resend:disabled > * {
    cursor: default;
  }

  .verify-phone-number .dialog .resend > span {
    display: none;
  }

  .verify-phone-number .dialog .resend:disabled > span {
    display: unset;
  }

  .verify-phone-number .dialog .resend * {
    color: rgb(34, 84, 156);
    cursor: pointer;
  }

  .verify-phone-number .dialog .resend:hover {
    color: rgb(34, 84, 156);
  }

  .verify-phone-number .dialog .resend:hover * {
    color: rgb(34, 84, 156);
  }

  .verify-phone-number .dialog .resend:disabled,
  .verify-phone-number .dialog .resend:disabled * {
    color: rgb(175, 175, 175);
  }

  .verify-phone-number .dialog .action {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }

  .verify-phone-number .dialog .close {
    border: none;
    background-color: rgb(74, 74, 74);
    border-radius: 24px;
    color: rgb(255, 255, 255);
    height: 48px;
    padding: 0px 24px;
    box-sizing: border-box;
    font-size: 18px;
    width: 100%;
    margin: 16px 0px 0px 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  .verify-phone-number .dialog .cancel {
    border: none;
    background-color: rgb(74, 74, 74);
    border-radius: 24px;
    color: rgb(255, 255, 255);
    height: 48px;
    padding: 0px 24px;
    box-sizing: border-box;
    font-size: 18px;
    width: calc((100% - 16px) / 2);
    margin: 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  .verify-phone-number .dialog .submit {
    border: none;
    background-image: linear-gradient(-180deg, rgb(245, 166, 35) 0%, rgb(251, 147, 73) 100%);
    border-radius: 24px;
    color: rgb(255, 255, 255);
    height: 48px;
    padding: 0px 24px;
    box-sizing: border-box;
    font-size: 18px;
    width: calc((100% - 16px) / 2);
    margin: 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  .verify-phone-number .dialog .submit:disabled {
    cursor: default;
  }

  .detail {
    background-color: rgb(34, 84, 156);
    padding: 0px 16px 43px 16px;
    display: flex;
    flex-direction: column;
  }

  .check-detail {
    padding: 8px 16px 64px 16px;
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    height: 100%;
    overflow-y: auto;
  }

  .check-detail .image-list {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    overflow: hidden;
    overflow-x: auto;
    margin-top: 10px;
  }

  .check-detail .image-list::-webkit-scrollbar-thumb {
    border: 2px solid rgb(255, 255, 255);
  }

  .check-detail .image-list .image {
    width: 64px;
    height: 64px;
    overflow: hidden;
    border-radius: 2px;
    margin-right: 2px;
    flex: 0 0 64px;
    cursor: pointer;
  }

  .check-detail .image-list .image img {
    width: 64px;
    height: 64px;
    object-fit: cover;
    overflow: hidden;
    display: block;
  }

  .two-fields-in-row {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;
    min-height: max-content;
  }

  .two-fields-in-row > .field {
    width: 50%;
  }

  .field {
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
    position: relative;
    align-items: stretch;
    min-height: max-content;
  }

  .field > label {
    background-color: rgba(255, 255, 255, 0.75);
    border-radius: 4px 0px 0px 4px;
    width: 80px;
    font-size: 14px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    margin: 0px 2px 0px 0px;
    min-height: 36px;
    padding-top: 8px;
    box-sizing: border-box;
    font-family: Prompt;
    color:rgb(34, 84, 156);
    text-align: center;
  }

  .field > label.small {
    padding-top: 0px;
    font-size: 12px;
    align-items: center;
  }

  .field > .input-list {
    display: block;
    flex-direction: column;
    width: calc(100% - 84px);
    min-height: 36px;
  }

  .field > .input-list > :not(:first-child) {
    margin-top: 2px;
  }

  .place-category-dropdown,
  .ref-place-dropdown {
    background-color: rgb(255, 255, 255);
    border-radius: 0px 4px 4px 0px;
    border: none;
    height: 36px;
    margin: 0px;
    box-sizing: border-box;
    padding: 0px 10px;
  }

  .field > .input-list input {
    background-color: rgb(255, 255, 255);
    border-radius: 0px 4px 4px 0px;
    border: none;
    height: 36px;
    padding: 3px 10px;
    font-size: 16px;
    margin: 0px;
    box-sizing: border-box;
    line-height: 36px;
    font-family: Default-PromptPai-Medium;
  }

  .field > .input-list input::placeholder {
    color: rgb(175, 175, 175);
    font-family: Default-PromptPai-Medium;
    font-size: 15px;
  }

  .field > .input-list .textarea {
    height: 60px;
    width: 100%;
    position: relative;
  }

  .field > .input-list textarea {
    background-color: rgb(255, 255, 255);
    border-radius: 0px 4px 4px 0px;
    border: none;
    padding: 3px 10px;
    font-size: 16px;
    margin: 0px;
    box-sizing: border-box;
    height: 60px;
    width: 100%;
    resize: none;
    line-height: 1.75;
    font-family: Default-PromptPai-Medium;
  }

  .field > .input-list textarea + .invalid {
    position: absolute;
    top: 0px;
    right: 3px;
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .field > .input-list textarea + .invalid img {
    width: 24px;
    height: 24px;
  }

  .field > .input-list textarea + .invalid .guide {
    position: absolute;
    bottom: -4px;
    right: -4px;
    transform: translate(0%, 100%);
    color: rgb(255, 255, 255);
    z-index: 1;
    background-color: rgb(209 11 15);
    padding: 3px 8px;
    border-radius: 4px !important;
    font-size: 12px;
    font-family: Prompt;
    box-shadow: 0px 0px 1px 2px rgba(0, 0, 0, 0.2);
    width: max-content;
    z-index: 12;
  }

  .field > .input-list textarea + .invalid .guide:after {
    z-index: -99;
    content: "";
    position: absolute;
    width: 0px;
    height: 0px;
    bottom: 0;
    top: 1px;
    right: 0px;
    box-sizing: border-box;
    border: 6px solid rgb(209 11 15);
    border-color: transparent transparent rgb(209 11 15) rgb(209 11 15);
    transform-origin: 0 0;
    transform: rotate(135deg);
  }

  .field > .input-list textarea::placeholder {
    color: rgb(175, 175, 175);
    font-family: Default-PromptPai-Medium;
    font-size: 15px;
  }

  .field > .input-list textarea::-webkit-scrollbar-thumb {
    border: 2px solid rgb(255, 255, 255);
  }

  .field > .icon-container {
    margin-right: 6px;
  }

  .field > .icon-container > img {
    width: 20px;
    height: 20px;
  }

  .require-verification-button {
    position: relative;
  }

  .require-verification-button .guide {
    position: absolute;
    bottom: -4px;
    right: -4px;
    transform: translate(0%, 100%);
    color: rgb(255, 255, 255);
    z-index: 1;
    background-color: rgb(209 11 15);
    padding: 3px 8px;
    border-radius: 4px !important;
    font-size: 12px;
    font-family: Prompt;
    box-shadow: 0px 0px 1px 2px rgba(0, 0, 0, 0.2);
    width: max-content;
  }

  .require-verification-button .guide:after {
    z-index: -99;
    content: "";
    position: absolute;
    width: 0px;
    height: 0px;
    bottom: 0;
    top: 1px;
    right: 0px;
    box-sizing: border-box;
    border: 6px solid rgb(209 11 15);
    border-color: transparent transparent rgb(209 11 15) rgb(209 11 15);
    transform-origin: 0 0;
    transform: rotate(135deg);
  }

  .tel-guide {
    position: absolute;
    bottom: -4px;
    right: -8px;
    transform: translate(0%, 100%);
    color: rgb(255, 255, 255);
    z-index: 1;
    background-color: rgb(59, 144, 255);
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-family: Prompt;
    box-shadow: 0px 0px 1px 2px rgba(0, 0, 0, 0.2);
  }

  .tel-guide:after {
    z-index: -99;
    content: "";
    position: absolute;
    width: 0px;
    height: 0px;
    bottom: 0;
    top: 0px;
    right: 6px;
    box-sizing: border-box;
    border: 6px solid rgb(59, 144, 255);
    border-color: transparent transparent rgb(59, 144, 255) rgb(59, 144, 255);
    transform-origin: 0 0;
    transform: rotate(135deg);
  }

  .tel-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    background-color: rgb(255, 255, 255);
    min-height: 36px;
  }

  .tel-row:first-child {
    border-top-right-radius: 4px;
  }

  .tel-row:last-child {
    border-bottom-right-radius: 4px;
  }

  .tel-row .promptpai-icon {
    padding: 0px 0px 0px 3px;
  }

  .tel-row .promptpai-icon img {
    filter: grayscale(1);
    opacity: 0.65;
  }

  .tel-row.selected .promptpai-icon img {
    filter: grayscale(0);
    opacity: 1;
  }

  .tel-row input {
    border-radius: 0px !important;
    width: calc(100% - (36px * 3));
    margin-right: auto !important;
  }

  .tel-row .confirm-verification {
    width: 36px;
    height: 36px;
    border: none;
    padding: 0px;
    background-color: rgb(255, 255, 255);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .tel-row .confirm-verification img {
    max-width: 24px;
    max-height: 24px;
  }

  .tel-row button {
    width: 36px;
    height: 36px;
    border: none;
    padding: 0px;
    background-color: rgb(255, 255, 255);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .tel-row button:disabled {
    filter: grayscale(1);
  }

  .tel-row button img {
    max-width: 24px;
    max-height: 24px;
    cursor: pointer;
  }

  .tel-row button:disabled img {
    opacity: 0.25;
  }

  .tel-row :last-child {
    border-radius: 0px 4px 4px 0px;
  }

  button.add-tel {
    margin-left: 72px;
    margin-bottom: 8px;
    width: calc(100% - 72px);
    border: none;
    background-color: transparent;
    color: rgb(255, 255, 255);
    font-size: 14px;
    font-family: Prompt;
    cursor: pointer;
    padding: 0px;
    min-height: 21px;
  }

  button.more-detail-button {
    border: none;
    background-color: transparent;
    color: rgb(255, 255, 255);
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;
    height: 39px;
    width: 100%;
    top: 126px;
    left: 0px;
    z-index: 10;
    /* background-image: linear-gradient(-180deg, rgb(245, 166, 35) 0%, rgb(251, 147, 73) 100%); */
    background-color: rgb(34, 84, 156);
    transition: top 0.3s ease 0s;
    box-shadow: 0px 2px 2px 1px rgba(0, 0, 0, 0.2);
    font-family: Prompt;
  }

  .hide-place-name button.more-detail-button {
    top: calc(112px - 74px);
  }

  button.more-detail-button.active.can-add-image {
    top: 327px;
  }

  .hide-place-name button.more-detail-button.active.can-add-image {
    top: calc(327px - 74px);
  }

  button.more-detail-button.active {
    top: 286px;
    background-color: transparent;
    box-shadow: unset;
  }

  .hide-place-name button.more-detail-button.active {
    top: calc(286px - 74px);
  }

  button.more-detail-button > img {
    width: 16px;
    height: 16px;
    top: 38px;
    left: calc(50% - 18px);
    background-color: rgb(34, 84, 156);
    padding: 5px 10px 0px 10px;
    border-radius: 18px 18px 0px 0px;
    transition: top 0.3s ease 0s, -ms-transform 0.3s ease 0s;
    box-shadow: 0px -3px 2px 1px rgba(0, 0, 0, 0.2);
  }

  button.more-detail-button.active > img {
    padding: 10px 10px 10px 10px;
    border-radius: 18px 18px 18px 18px;
    top: 0px;
    box-shadow: 0px 0px 12px 0px rgba(255, 255, 255, 1);
  }

  button.more-detail-button > img.down {
    -ms-transform: rotate(180deg); /* IE 9 */ -webkit-transform: rotate(180deg); /* Safari */ transform: rotate(180deg);
  }

  .more-detail {
    background-image: linear-gradient(-180deg, rgba(34, 84, 156, 1) 0%, rgba(34, 84, 156, 1) 40%, rgba(34, 84, 156, 0.6) 90%, rgba(34, 84, 156, 0) 100%);
    padding: 0px 16px 0px 16px;
    display: flex;
    flex-direction: column;
    position: absolute;
    z-index: 10;
    top: 130px;
    left: 0px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    transition: height 0.3s ease 0s;
    height: 0px;
  }

  .hide-place-name .more-detail {
    top: calc(130px - 74px);
  }

  .more-detail.active.can-add-image {
    height: calc(100% - 190px);
  }

  .hide-place-name .more-detail.active.can-add-image {
    height: calc(100% - 190px + 74px);
  }

  .more-detail.active {
    height: calc(100% - 190px);
  }

  .hide-place-name .more-detail.active {
    height: calc(100% - 190px + 74px);
  }

  .warning-privacy {
    font-family: Prompt;
    color: rgb(220, 20, 60);
    font-size: 14px;
    background-color: rgba(220, 20, 60, 0.1);
    padding: 6px;
    border-radius: 3px;
    border: 1px solid rgb(220, 20, 60);
  }

  .input-mode-button {
    background-color: rgb(200, 212, 230);
    border: none;
    border-radius: 18px;
    font-size: 14px;
    margin: 8px 8px 0px 0px;
    box-sizing: border-box;
    height: 36px;
    width: calc(50% - 9px);
    resize: none;
    font-family: Prompt;
    color: rgb(34, 84, 156);
    cursor: pointer;
    box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.15);
  }

  .add-image-button {
    background-color: rgb(200, 212, 230);
    border: none;
    border-radius: 18px;
    font-size: 14px;
    margin: 8px 0px 0px 8px;
    box-sizing: border-box;
    height: 36px;
    width: calc(50% - 9px);
    resize: none;
    font-family: Prompt;
    color: rgb(34, 84, 156);
    cursor: pointer;
    box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.15);
  }

  button.reset-address,
  button.update-address {
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: calc(70px - 4px);
    margin: 2px;
    padding: 4px 0px;
    background-color: rgb(74, 74, 74);
    border: none;
    border-radius: 4px;
    color: rgb(255, 255, 255);
    cursor: pointer;
    box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.2);
  }

  button.reset-address:active,
  button.update-address:active {
    box-shadow: none;
  }

  .text-field-suggest {
    background-color: rgb(255, 255, 255);
    border-radius: 0px 4px 4px 0px;
    border: none;
    height: 36px;
    margin: 0px;
    box-sizing: border-box;
    padding: 0px 10px;
  }

  #create-place-map {
    width: 100%;
    height: calc(100% - 173px);
    position: relative;
  }

  .hide-place-name #create-place-map {
    height: calc(100% - 89px);
  }

  button.current-location-button,
  button.zoom-in-button,
  button.zoom-out-button {
    background-color: #22549C;
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid rgb(255, 255, 255);
    border-radius: 50%;
    padding: 0px;
    margin: 8px 0px 0px 0px;
    cursor: pointer;
  }

  button.zoom-out-button {
    margin-bottom: 36px;
  }

  button.current-location-button > img,
  button.zoom-in-button > img,
  button.zoom-out-button > img {
    width: 16px;
    height: 16px;
    display: block;
  }

  .edit-location-pin {
    position: absolute;
    z-index: 4;
    top: calc(50% - 39px);
    left: calc(50% - 27px / 2);
  }

  button.lock-location-button {
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin: 2px 0px 0px 4px;
    border: none;
    background-color: transparent;
    padding: 0px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  button.lock-location-button > img {
    width: 24px;
    height: 24px;
  }

  .button-list {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
    position: absolute;
    bottom: 0px;
    width: 100%;
    background-image: linear-gradient(-180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  }

  button.create-button {
    border: none;
    background-image: linear-gradient(-180deg, rgb(245, 166, 35) 0%, rgb(251, 147, 73) 100%);
    border-radius: 24px;
    color: rgb(255, 255, 255);
    height: 48px;
    padding: 0px 24px;
    box-sizing: border-box;
    font-size: 18px;
    width: calc(100% - 16px);
    margin: 8px 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  button.back-button {
    border: none;
    background-color: rgb(74, 74, 74);
    border-radius: 24px;
    color: rgb(255, 255, 255);
    height: 48px;
    padding: 0px 24px;
    box-sizing: border-box;
    font-size: 18px;
    width: calc(50% - 12px);
    margin: 8px 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  button.confirm-button {
    border: none;
    background-image: linear-gradient(-180deg, rgb(245, 166, 35) 0%, rgb(251, 147, 73) 100%);
    border-radius: 24px;
    color: rgb(255, 255, 255);
    height: 48px;
    padding: 0px 24px;
    box-sizing: border-box;
    font-size: 18px;
    width: calc(50% - 12px);
    margin: 8px 0px;
    cursor: pointer;
    font-family: Prompt;
  }

  button.confirm-button:disabled {
    background-image: none;
    background-color: rgb(175, 175, 175);
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 2s;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .add-place-image-container {
    position: absolute;
    top: 50px;
    left: 0px;
    width: 100%;
    height: calc(100% - 50px);
    z-index: 10;
    overflow: hidden;
    overflow-y: auto;
    background-color: rgb(255, 255, 255);
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    padding-bottom: 64px;
    box-sizing: border-box;
  }

  .add-place-image-container::-webkit-scrollbar {
    background-color: rgb(255, 255, 255);
  }
  .add-place-image-container::-webkit-scrollbar-thumb {
    border: 2px solid rgb(255, 255, 255);
  }

  .add-place-image-container > .image {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 320px;
    padding: 16px;
    box-sizing: border-box;
    cursor: pointer;
  }

  .add-place-image-container > .image.add {
    justify-content: center;
    cursor: pointer;
    width: calc(320px - 32px);
    height: calc(320px - 32px);
    padding: 0px;
    margin: 16px;
    border-radius: 8px;
    border: 2px solid rgb(34, 84, 156);
  }

  .add-place-image-container > .image > img {
    width: calc(320px - 32px);
    height: calc(320px - 32px);
    object-fit: cover;
    box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    overflow: hidden;
  }

  .add-place-image-container > .image.add > img {
    object-fit: contain;
    box-shadow: none;
    width: 180px;
    height: 180px;
  }

  .add-place-image-container > .image > .row {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    margin: 16px 0px 0px 0px;
    width: 100%;
    position: relative;
  }

  .add-place-image-container > .image input {
    width: 100%;
    border: 1px solid rgb(227, 227, 227);
    border-radius: 8px;
    box-sizing: border-box;
    margin: 0px;
    padding: 4px 8px;
    font-size: 16px;
    line-height: 1.75;
  }

  .add-place-image-container > .image input::placeholder {
    color: rgb(175, 175, 175);
    font-size: 15px;
    line-height: 1.75;
  }

  .edit-icon {
    position: absolute;
    top: -52px;
    left: 12px;
    width: 24px;
    height: 24px;
    border-radius: 12px;
    box-sizing: border-box;
    filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.25));
    background-color: rgba(255, 255, 255, 0.9);
  }

  .delete-button {
    width: 36px;
    height: 36px;
    padding: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    background-color: transparent;
    cursor: pointer;
  }

  .delete-button > img {
    width: 28px;
    height: 28px;
  }

  .back-button.on-add-place-image-container {
    position: absolute;
    left: 8px;
    bottom: 0px;
    z-index: 11;
    box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.4);
    width: calc(100% - 16px);
  }

  @media only screen and (max-width: 768px) {
    .create-place-form {
      width: 100%;
      min-width: 320px;
      height: 100%;
      border-radius: 0px;
    }

    .body {
      height: calc(100% - 50px);
    }
  }

  @media only screen and (max-width: 480px) {
    .two-fields-in-row > .field {
      width: 100%;
    }

    .input-mode-button,
    .add-image-button {
      width: calc(100% - 2px);
      margin: 8px 0px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/CreatePlaceForm.vue